/**
 * Copy de las cuatro páginas de Soluciones: Restauración, Clínicas,
 * Automotriz y Hoteles.
 *
 * Las cuatro son LA MISMA plantilla de siete secciones —se comprobó leyendo
 * los cuatro `.dc.html` enteros— así que las pinta un solo `SectorV3.jsx`. Aquí
 * solo va lo que cambia: el copy, los cuatro locales del hero y el widget del
 * bloque «Por qué importa», que es distinto en cada uno (un podio, una lista de
 * idiomas, una comparativa, una balanza).
 *
 * ────────────────────────────────────────────────────────────────────────────
 * TRES AFIRMACIONES DEL DISEÑO QUE NO SE COPIAN
 * ────────────────────────────────────────────────────────────────────────────
 *
 * 1. **«Se repite cada semana o cada mes»**, en las FAQ de Restauración y de
 *    Automotriz. Es la misma promesa que ya se quitó de Publicaciones IA: la
 *    recurrencia NO EXISTE, un programado se publica una vez. Se sustituye por
 *    la antelación, que sí es real.
 * 2. **«Configura las reglas de respuesta POR IDIOMA»**, en Hoteles. No hay
 *    reglas por idioma: hay un interruptor por regla
 *    (`reply_in_review_lang`) que hace que la IA conteste en el idioma en que
 *    se escribió la reseña, y solo funciona en modo IA. Es casi lo mismo de
 *    cara al hotelero, pero se cuenta como lo que es.
 * 3. **«Las respuestas automáticas nunca entran en detalles de ningún caso»**,
 *    en Clínicas. Es la más delicada de todas: va dirigida a sanitarios y suena
 *    a garantía, y NO LA GARANTIZA NADA — el prompt no lleva ninguna
 *    instrucción sobre privacidad, solo el tono de marca que escribe el
 *    usuario. Se reescribe contando lo que sí se puede prometer porque es
 *    configuración y no comportamiento de un modelo: el tono se fija una vez, y
 *    las puntuaciones bajas se pueden dejar en «solo aviso» para que ninguna
 *    reseña sensible reciba respuesta automática.
 *
 * Lo demás encaja: los cuatro niveles del mock del hero («Líder local», «Bien
 * posicionado», «Camino al podio», «Empezando») son los reales de
 * `healthScore.level.*`, y el «ranking entre tus locales» de las FAQ existe
 * (`gbusiness.healthScore.rankingTitle`, con su «Mejor» y su «A mejorar»).
 */

/*
 * Las tres tarjetas de «por qué este sector lo necesita» apuntan siempre a las
 * mismas tres páginas de Producto; solo cambia el argumento.
 *
 * La URL NO se escribe aquí: `clave` es la de `lib/rutasPublicas.js` y el
 * destino lo saca `SectorV3` del idioma que se esté sirviendo. Antes había una
 * ruta fija repetida en los cinco idiomas, así que la tarjeta de la página
 * alemana llevaba a la página de producto en español.
 */

/**
 * Los cuatro niveles que enseña el mock del hero NO son copy de la landing: son
 * los que escribe el panel (`gbusiness.healthScore.level.*`).
 *
 * Se escriben una vez por idioma y los cuatro sectores los reutilizan. Sueltos
 * serían DIECISÉIS cadenas por idioma —sesenta y cuatro en total— y bastaría
 * con que alguien retocara una para que Restauración y Hoteles llamaran
 * distinto al mismo nivel, que es la clase de incoherencia que nadie reporta
 * porque cada página, por separado, está bien.
 */
const NIVELES = {
  es: { lider: 'Líder local', bien: 'Bien posicionado', podio: 'Camino al podio', inicio: 'Empezando' },
  en: { lider: 'Local leader', bien: 'Well positioned', podio: 'On the way up', inicio: 'Just starting' },
  pt: { lider: 'Líder local', bien: 'Bem posicionado', podio: 'Caminho do pódio', inicio: 'A começar' },
  de: { lider: 'Lokaler Marktführer', bien: 'Gut positioniert', podio: 'Auf dem Weg nach oben', inicio: 'Am Anfang' },
  fr: { lider: 'Leader local', bien: 'Bien positionné', podio: 'En route vers le podium', inicio: 'Au démarrage' },
};

/** Lo que las cuatro dicen igual. Cuatro copias de esto es lo que hace que
 *  dentro de un año una diga «7 días» y otra «14». */
const comun = {
  verProducto: 'Ver',
  panelEnlace: 'Ver Multiubicaciones',
  faqTitulo: 'Preguntas frecuentes',
  cierreCta: 'Probar 7 días gratis',
  cierreMicrocopy: '7 días gratis · sin compromiso · cancelas cuando quieras',
  cierreEnterpriseA: '¿Más de 30 locales? ',
  cierreEnterpriseCta: 'Contacta con ventas',
  kickerPorQue: 'Por qué importa',
};

const restauracion = {
  titulo: 'Restauración',
  hero: {
    badge: 'Para grupos de restauración con más de un local',
    titulo: 'Posiciona cada uno de tus restaurantes como el ',
    acento: 'Nº1 de su zona',
    sub: 'Responde reseñas, publica ofertas y vigila tu posición en Google Maps en todos tus locales a la vez, sin repetir el trabajo en cada uno.',
    cta: 'Probar 7 días gratis',
    ctaMulti: 'Ver cómo funciona con varios locales',
    listaTitulo: 'Tus locales',
    listaEtiqueta: '4 restaurantes',
    aviso: 'Local Norte es el que más necesita atención ahora',
    locales: [
      { clave: 'centro', n: 'Local Centro', i: 'C', nivel: NIVELES.es.lider, p: '84', tono: 'bien' },
      { clave: 'puerto', n: 'Local Puerto', i: 'P', nivel: NIVELES.es.bien, p: '71', tono: 'bien' },
      { clave: 'ensanche', n: 'Local Ensanche', i: 'E', nivel: NIVELES.es.podio, p: '58', tono: 'medio' },
      { clave: 'norte', n: 'Local Norte', i: 'N', nivel: NIVELES.es.inicio, p: '34', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Con un local, se nota si pierdes posiciones en Google Maps. Con diez, no sabes cuál se está cayendo.',
    p1: 'En restauración las reseñas no paran de llegar. Cada turno, cada fin de semana, cada evento suma reseñas nuevas, en todos tus locales a la vez. Y cada una que se queda sin responder no es solo una tarea pendiente, es una señal que Google usa para decidir en qué posición apareces cuando alguien busca un restaurante como el tuyo.',
    p2: 'Con un solo local, ya cuesta mantener eso al día. Con varios, el problema se multiplica: mientras un local mantiene su puesto, otro puede estar cayendo posiciones sin que nadie lo note, porque nadie está mirando los diez a la vez. Cada sede compite por su cuenta contra los restaurantes de su misma calle.',
  },
  razones: {
    kicker: 'Por qué restauración lo necesita',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilidad SEO',
        d: 'Casi nadie busca un restaurante por su nombre antes de haber ido. Busca «sushi cerca de mí» o «dónde comer bien por aquí», y ahí es donde te juegas si te encuentran o no.',
      },
      {
        clave: 'automatizaciones', t: 'Automatizaciones',
        d: 'Con el volumen de reseñas que mueve un restaurante entre semana y fin de semana, responder a mano no escala. Las positivas se responden solas, y si llega una mala, te enteras en minutos, no días.',
      },
      {
        clave: 'publicacionesIa', t: 'Publicaciones IA',
        d: 'El menú cambia, hay ofertas de fin de semana, eventos con música en vivo. Publícalo en segundos y prográmalo con antelación en todos tus locales a la vez.',
      },
    ],
  },
  panel: {
    titulo: 'Un panel, todos tus restaurantes',
    cuerpo: 'Comparas el rendimiento de cada local, ves cuál necesita atención primero y aplicas las mismas reglas a todos sin configurarlas uno por uno. El detalle completo de cómo funciona la gestión multi-local está en Multiubicaciones.',
  },
  porQue: {
    titulo: 'En restauración, la búsqueda genérica pesa más que en casi ningún otro sector',
    cuerpo: 'Rara vez alguien busca un restaurante por su nombre antes de haber ido, sobre todo si está de paso, de turismo o probando algo nuevo en un barrio que no conoce. Eso hace que aparecer en el pack local, los tres primeros resultados de Google Maps, importe todavía más aquí que en negocios donde el cliente ya sabe a quién busca.',
    widget: 'podio',
    pie: 'Solo los tres primeros se ven sin desplegar',
  },
  faq: [
    {
      q: '¿Sirve para un solo restaurante o hace falta tener varios locales?',
      a: 'Funciona igual de bien para uno solo. Todo lo que ves aquí se aplica igual; la gestión multi-local es un extra si tienes más de un local, no un requisito.',
    },
    {
      q: '¿Funciona igual para un restaurante nuevo, con pocas reseñas todavía?',
      a: 'Sí. El escáner y el análisis SEO funcionan desde el primer día, y cuantas más reseñas vayas sumando, más señales tiene el sistema para ayudarte a subir.',
    },
    {
      // El diseño prometía aquí la repetición semanal, que no existe.
      q: '¿Puedo programar publicaciones de ofertas de fin de semana con antelación?',
      a: 'Sí, eliges la fecha y la hora y se publica sola, hasta con 180 días de antelación. Si la oferta se repite, se duplica la publicación anterior y se le cambia la fecha.',
    },
    {
      q: 'Tengo varios locales, ¿puedo comparar el rendimiento entre ellos?',
      a: 'Sí, hay un ranking entre tus locales por puntuación SEO: ves de un vistazo cuál va mejor y cuál necesita atención, y entras al análisis completo del que quieras.',
    },
    {
      q: '¿El widget de reseñas sirve si mi web es solo de reservas, sin blog ni nada más?',
      a: 'Sí, es un fragmento de código que pegas donde quieras de tu web, no necesita ninguna estructura especial detrás.',
    },
  ],
  cierre: {
    titulo: 'Que cada uno de tus restaurantes compita por ser el ',
    acento: 'Nº1 de su zona',
    sub: 'Conecta tus fichas de Google, escanea cada zona y deja que las reseñas y las publicaciones se gestionen solas en todos tus locales.',
  },
};

const clinicas = {
  titulo: 'Clínicas',
  hero: {
    badge: 'Para clínicas y centros de salud con varias sedes',
    titulo: 'Posiciona cada una de tus clínicas como el ',
    acento: 'Nº1 de su zona',
    sub: 'Responde reseñas con prudencia profesional, publica novedades y vigila tu posición en Google Maps en todas tus sedes a la vez, sin repetir el trabajo en cada una.',
    cta: 'Probar 7 días gratis',
    ctaMulti: 'Ver cómo funciona con varias sedes',
    listaTitulo: 'Tus sedes',
    listaEtiqueta: '4 clínicas',
    aviso: 'Sede Norte es la que más necesita atención ahora',
    locales: [
      { clave: 'centro', n: 'Sede Centro', i: 'C', nivel: NIVELES.es.lider, p: '86', tono: 'bien' },
      { clave: 'este', n: 'Sede Este', i: 'E', nivel: NIVELES.es.bien, p: '73', tono: 'bien' },
      { clave: 'ensanche', n: 'Sede Ensanche', i: 'S', nivel: NIVELES.es.podio, p: '57', tono: 'medio' },
      { clave: 'norte', n: 'Sede Norte', i: 'N', nivel: NIVELES.es.inicio, p: '31', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Con una clínica, se nota si pierdes posiciones en Google Maps. Con varias, no sabes cuál se está cayendo.',
    p1: 'Cuando alguien busca «dentista cerca de mí» o «fisioterapeuta en tu zona», casi nunca mira más allá de los primeros resultados. Y cada reseña que se queda sin responder es una señal que Google usa para decidir si tu clínica aparece ahí o no.',
    p2: 'Con una sola clínica, ya cuesta mantenerlo al día. Con varias sedes, el problema se multiplica: mientras una mantiene su posición, otra puede estar cayendo sin que nadie lo note. Cada sede compite sola en su propio barrio, y sin visión de conjunto no sabes cuál necesita ayuda hasta que ya ha perdido terreno frente a la clínica de al lado.',
  },
  razones: {
    kicker: 'Por qué clínicas lo necesita',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilidad SEO',
        d: 'Nadie busca «Clínica Dental García» antes de conocerla. Busca «dentista cerca de mí» o «fisioterapeuta en tu zona», y esas búsquedas son justo las que decide el Mapa SEO.',
      },
      {
        clave: 'automatizaciones', t: 'Automatizaciones',
        // Reescrito: el diseño garantizaba que la IA «nunca» entra en detalles
        // de un caso, y eso no lo asegura ninguna parte del sistema.
        d: 'Fijas el tono una vez —profesional y prudente— y se aplica a todas las respuestas. Y decides qué puntuaciones se responden solas: lo habitual aquí es automatizar las buenas y dejar las de 1 a 3 estrellas en «solo aviso», para que ninguna reseña delicada reciba una respuesta que no hayas escrito tú.',
      },
      {
        clave: 'publicacionesIa', t: 'Publicaciones IA',
        d: 'Un nuevo especialista que se incorpora, un cambio de horario, una campaña de revisiones. Se publica en segundos y se programa con antelación en todas tus sedes a la vez.',
      },
    ],
  },
  panel: {
    titulo: 'Un panel, todas tus clínicas',
    cuerpo: 'Comparas el rendimiento de cada sede, ves cuál necesita atención primero y aplicas el mismo tono profesional a todas sin configurarlo sede por sede. El detalle completo de cómo funciona la gestión multi-sede está en Multiubicaciones.',
  },
  porQue: {
    titulo: 'En salud, la decisión se piensa más, y se compara más',
    cuerpo: 'Elegir dentista o fisioterapeuta no es una decisión rápida. La mayoría compara varias opciones antes de decidir, y las reseñas junto con la posición en Google Maps son de lo primero que miran para reducir esa lista. Aparecer entre los primeros no solo trae más visitas a tu ficha: filtra directamente cuántas opciones te van a comparar antes de elegir.',
    widget: 'comparativa',
    pie: 'La lista que compara antes de pedir cita',
    items: [
      { clave: 'tuya', n: 'Tu clínica', destacada: true },
      { clave: 'allado', n: 'Clínica de al lado' },
      { clave: 'barrio', n: 'Centro del barrio' },
      { clave: 'otra', n: 'Otra opción', atenuada: true },
    ],
  },
  faq: [
    {
      q: '¿Sirve para una sola clínica o hace falta tener varias sedes?',
      a: 'Funciona igual de bien para una sola. La gestión multi-sede es un extra si tienes más de una, no un requisito.',
    },
    {
      // El diseño respondía con una garantía que el sistema no da.
      q: '¿Qué control tengo sobre lo que publica una respuesta automática?',
      a: 'El que tú configures. Eliges exactamente qué puntuaciones se responden solas y cuáles se quedan en aviso por correo para que las contestes tú, y fijas el tono de marca que usa la IA. Lo habitual en salud es automatizar solo las reseñas buenas y tratar a mano las de 1 a 3 estrellas.',
    },
    {
      q: '¿Puedo publicar cuando se incorpora un nuevo especialista o hay una campaña?',
      a: 'Sí, programas la publicación con antelación, o la dejas para publicar al momento.',
    },
    {
      q: 'Tengo varias sedes, ¿puedo comparar el rendimiento entre ellas?',
      a: 'Sí, hay un ranking entre tus sedes por puntuación SEO: ves de un vistazo cuál va mejor y cuál necesita atención, sin entrar sede por sede.',
    },
    {
      q: '¿El widget de reseñas funciona si mi web tiene reserva de cita online?',
      a: 'Sí, es un fragmento de código que pegas donde quieras de tu web, junto al botón de reserva si quieres.',
    },
  ],
  cierre: {
    titulo: 'Que cada una de tus clínicas compita por ser el ',
    acento: 'Nº1 de su zona',
    sub: 'Conecta tus fichas de Google, escanea cada zona y deja que las reseñas y las publicaciones se gestionen solas en todas tus sedes.',
  },
};

const automotriz = {
  titulo: 'Automotriz',
  hero: {
    badge: 'Para talleres y concesionarios con varias sedes',
    titulo: 'Posiciona cada uno de tus talleres como el ',
    acento: 'Nº1 de su zona',
    sub: 'Responde reseñas, publica tus campañas de temporada y vigila tu posición en Google Maps en todas tus sedes a la vez, sin repetir el trabajo en cada una.',
    cta: 'Probar 7 días gratis',
    ctaMulti: 'Ver cómo funciona con varias sedes',
    listaTitulo: 'Tus sedes',
    listaEtiqueta: '4 talleres',
    aviso: 'Taller Norte es el que más necesita atención ahora',
    locales: [
      { clave: 'centro', n: 'Taller Centro', i: 'C', nivel: NIVELES.es.lider, p: '85', tono: 'bien' },
      { clave: 'poligono', n: 'Taller Polígono', i: 'P', nivel: NIVELES.es.bien, p: '72', tono: 'bien' },
      { clave: 'este', n: 'Taller Este', i: 'E', nivel: NIVELES.es.podio, p: '56', tono: 'medio' },
      { clave: 'norte', n: 'Taller Norte', i: 'N', nivel: NIVELES.es.inicio, p: '33', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Con un taller, se nota si pierdes posiciones en Google Maps. Con varios, no sabes cuál se está cayendo.',
    p1: 'Cuando el coche falla o toca revisión, casi nadie tiene un taller de cabecera de toda la vida. Busca «taller cerca de mí» o «cambio de neumáticos en tu zona», y decide entre los primeros resultados que le salen. Cada reseña que se queda sin responder es una señal que Google usa para decidir si apareces ahí.',
    p2: 'Con un solo taller, ya cuesta mantenerlo al día. Con varias sedes, el problema se multiplica: mientras una mantiene su posición, otra puede estar cayendo sin que nadie lo note. Cada sede compite sola en su propio barrio, y sin visión de conjunto no sabes cuál necesita ayuda hasta que ya ha perdido terreno frente al taller de al lado.',
  },
  razones: {
    kicker: 'Por qué automotriz lo necesita',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilidad SEO',
        d: 'Nadie busca tu taller por nombre antes de haber ido. Busca «taller cerca de mí» o «revisión ITV en tu zona», y esas búsquedas son justo las que decide el Mapa SEO.',
      },
      {
        clave: 'automatizaciones', t: 'Automatizaciones',
        d: 'Cambios de aceite, revisiones, ITV: cada visita genera una reseña, y el volumen se dispara en temporada. Las positivas se responden solas, y si llega una mala, te enteras en minutos, no días.',
      },
      {
        clave: 'publicacionesIa', t: 'Publicaciones IA',
        d: 'Cambio de neumáticos de temporada, revisión gratuita antes de vacaciones, una nueva marca que empiezas a llevar. Publícalo en segundos y prográmalo con antelación en todas tus sedes a la vez.',
      },
    ],
  },
  panel: {
    titulo: 'Un panel, todos tus talleres',
    cuerpo: 'Comparas el rendimiento de cada sede, ves cuál necesita atención primero y aplicas las mismas reglas a todas sin configurarlas una por una. El detalle completo de cómo funciona la gestión multi-sede está en Multiubicaciones.',
  },
  porQue: {
    titulo: 'En automoción, la confianza pesa más que el precio',
    cuerpo: 'La mayoría de clientes no sabe de mecánica, así que no puede juzgar si el diagnóstico o el precio son justos. Lo único que tiene para decidir en quién confiar es lo que otros ya han contado: las reseñas y la posición en Google Maps hacen ese trabajo por él antes de que entre por la puerta.',
    widget: 'balanza',
    pie: 'Lo que de verdad le hace elegir',
    items: [
      { clave: 'confianza', l: 'Confianza', pct: 88, destacada: true },
      { clave: 'precio', l: 'Precio', pct: 34 },
    ],
  },
  faq: [
    {
      q: '¿Sirve para un solo taller o hace falta tener varias sedes?',
      a: 'Funciona igual de bien para uno solo. La gestión multi-sede es un extra si tienes más de una, no un requisito.',
    },
    {
      q: '¿Funciona igual para un taller nuevo, con pocas reseñas todavía?',
      a: 'Sí. El escáner y el análisis SEO funcionan desde el primer día, y cuantas más reseñas vayas sumando, más señales tiene el sistema para ayudarte a subir.',
    },
    {
      // Otra vez la recurrencia inventada del diseño.
      q: '¿Puedo programar las campañas de temporada con antelación?',
      a: 'Sí, eliges la fecha y la hora que quieras, hasta con 180 días de antelación, y se publica sola en todas las sedes que elijas.',
    },
    {
      q: 'Tengo varias sedes, ¿puedo comparar el rendimiento entre ellas?',
      a: 'Sí, hay un ranking entre tus sedes por puntuación SEO: ves de un vistazo cuál va mejor y cuál necesita atención, sin entrar sede por sede.',
    },
    {
      q: '¿El widget de reseñas funciona si mi web tiene cita previa online?',
      a: 'Sí, es un fragmento de código que pegas donde quieras de tu web, junto al botón de cita si quieres.',
    },
  ],
  cierre: {
    titulo: 'Que cada uno de tus talleres compita por ser el ',
    acento: 'Nº1 de su zona',
    sub: 'Conecta tus fichas de Google, escanea cada zona y deja que las reseñas y las publicaciones se gestionen solas en todas tus sedes.',
  },
};

const hoteles = {
  titulo: 'Hoteles',
  hero: {
    badge: 'Para hoteles y cadenas hoteleras con varios establecimientos',
    titulo: 'Posiciona cada uno de tus hoteles como el ',
    acento: 'Nº1 de su zona',
    sub: 'Responde reseñas en cualquier idioma, publica tus promociones de temporada y vigila tu posición en Google Maps en todos tus establecimientos a la vez, sin repetir el trabajo en cada uno.',
    cta: 'Probar 7 días gratis',
    ctaMulti: 'Ver cómo funciona con varios establecimientos',
    listaTitulo: 'Tus establecimientos',
    listaEtiqueta: '4 hoteles',
    aviso: 'Hotel Bahía es el que más necesita atención ahora',
    locales: [
      { clave: 'centro', n: 'Hotel Centro', i: 'C', nivel: NIVELES.es.lider, p: '87', tono: 'bien' },
      { clave: 'puerto', n: 'Hotel Puerto', i: 'P', nivel: NIVELES.es.bien, p: '74', tono: 'bien' },
      { clave: 'sierra', n: 'Hotel Sierra', i: 'S', nivel: NIVELES.es.podio, p: '59', tono: 'medio' },
      { clave: 'bahia', n: 'Hotel Bahía', i: 'B', nivel: NIVELES.es.inicio, p: '32', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Con un hotel, se nota si pierdes posiciones en Google Maps. Con varios, no sabes cuál se está cayendo.',
    p1: 'Quien busca hotel casi nunca es un cliente habitual. Es alguien que no conoce la ciudad y decide entre los primeros resultados que Google le muestra, sin ninguna fidelidad previa a la que agarrarse. Cada reseña sin responder es una señal que pesa en si apareces ahí o no.',
    p2: 'Con un solo hotel, ya cuesta mantenerlo al día, sobre todo en temporada alta, cuando las reseñas se disparan de golpe. Con varios establecimientos el problema se multiplica: mientras uno mantiene su posición, otro puede estar cayendo sin que nadie lo note, porque nadie está mirando las posiciones de todos a la vez.',
  },
  razones: {
    kicker: 'Por qué hoteles lo necesita',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilidad SEO',
        d: 'Casi nadie busca tu hotel por nombre la primera vez. Busca «hotel en [ciudad]» o «hotel cerca de [zona]», sin ninguna fidelidad previa, y esas búsquedas son justo las que decide el Mapa SEO.',
      },
      {
        clave: 'automatizaciones', t: 'Automatizaciones',
        // El diseño hablaba de «reglas por idioma», que no existen: lo que hay
        // es un interruptor que contesta en el idioma de la reseña.
        d: 'Las reseñas llegan en varios idiomas según de dónde venga cada huésped. Con el modo IA puedes hacer que cada respuesta salga en el idioma en que se escribió la reseña, manteniendo el mismo tono de marca. Y en temporada alta, cuando se disparan de golpe, ninguna se queda atrás.',
      },
      {
        clave: 'publicacionesIa', t: 'Publicaciones IA',
        d: 'Una oferta de última hora, un paquete de fin de semana, una nueva instalación del hotel. Publícalo en segundos y prográmalo con antelación en todos tus establecimientos a la vez.',
      },
    ],
  },
  panel: {
    titulo: 'Un panel, todos tus hoteles',
    cuerpo: 'Comparas el rendimiento de cada establecimiento, ves cuál necesita atención primero y aplicas las mismas reglas a todos sin configurarlas uno por uno. El detalle completo de cómo funciona la gestión multi-sede está en Multiubicaciones.',
  },
  porQue: {
    titulo: 'En hoteles, casi nadie decide con fidelidad previa',
    cuerpo: 'A diferencia de un restaurante de tu barrio o un taller de confianza, un hotel casi siempre se elige entre desconocidos, comparando varias opciones a la vez, muchas veces desde otra ciudad o país. Ahí es donde más pesa aparecer entre los primeros resultados y tener reseñas recientes y bien respondidas: es prácticamente lo único en lo que el viajero puede apoyarse para decidir.',
    widget: 'idiomas',
    pie: 'Reseñas de la misma semana, cuatro idiomas',
    items: [
      { clave: 'es', k: 'ES', t: 'Todo perfecto, volveremos…' },
      { clave: 'en', k: 'EN', t: 'Lovely stay, great staff…' },
      { clave: 'de', k: 'DE', t: 'Sehr schönes Hotel…' },
      { clave: 'fr', k: 'FR', t: 'Très bon accueil…' },
    ],
  },
  faq: [
    {
      q: '¿Sirve para un solo hotel o hace falta tener varios establecimientos?',
      a: 'Funciona igual de bien para uno solo. La gestión multi-sede es un extra si tienes más de un establecimiento, no un requisito.',
    },
    {
      // El diseño decía «reglas por idioma»; lo real es un interruptor.
      q: '¿Puede responder reseñas en el idioma en que las escriben?',
      a: 'Sí. En modo IA hay un ajuste que hace que la respuesta salga en el idioma de la reseña en vez de en el tuyo, así que un huésped alemán recibe su contestación en alemán sin que tengas que hacer nada.',
    },
    {
      q: '¿Aguanta bien los picos de reseñas de temporada alta?',
      a: 'Sí, las reglas de respuesta y las alertas funcionan igual haya una reseña al día o cincuenta.',
    },
    {
      q: 'Tengo varios establecimientos, ¿puedo comparar el rendimiento entre ellos?',
      a: 'Sí, hay un ranking entre tus establecimientos por puntuación SEO: ves de un vistazo cuál va mejor y cuál necesita atención, sin entrar uno por uno.',
    },
    {
      q: '¿El widget de reseñas funciona con mi motor de reservas propio?',
      a: 'Sí, es un fragmento de código que pegas donde quieras de tu web, junto al motor de reservas si quieres.',
    },
  ],
  cierre: {
    titulo: 'Que cada uno de tus hoteles compita por ser el ',
    acento: 'Nº1 de su zona',
    sub: 'Conecta tus fichas de Google, escanea cada zona y deja que las reseñas y las publicaciones se gestionen solas en todos tus establecimientos.',
  },
};

/*
 * ── LAS TRADUCCIONES ────────────────────────────────────────────────────────
 *
 * Las reglas generales están en `landingV3.home.js`. Tres avisos propios:
 *
 *  1. **`tono` no se traduce.** Vale `bien` / `medio` / `mal` y es lo que elige
 *     el color de la fila; traducirlo la deja gris.
 *  2. **Las reseñas de ejemplo de Hoteles TAMPOCO.** `hoteles.porQue.items[].t`
 *     son cuatro frases, cada una en un idioma distinto, y el widget existe
 *     precisamente para enseñar esa mezcla. Traducidas al idioma de la página
 *     se convierten en cuatro frases iguales bajo cuatro banderas distintas, y
 *     el bloque deja de decir nada.
 *  3. **La inicial `i` sigue al nombre `n`.** Es el avatar de la fila; si el
 *     local pasa a llamarse «Standort Hafen» la inicial es la H.
 *
 * Y lo de siempre en estas cuatro páginas: las FAQ dicen lo que el producto
 * hace. La cabecera del fichero enumera las tres afirmaciones del diseño que se
 * reescribieron por no ser ciertas —la recurrencia de las publicaciones, las
 * «reglas por idioma» y la garantía de privacidad en Clínicas—. Traducirlas
 * «como sonaban mejor» las vuelve a meter, en un idioma donde nadie las mira.
 */
const comunEn = {
  verProducto: 'See',
  panelEnlace: 'See Multi-location',
  faqTitulo: 'Frequently asked questions',
  cierreCta: 'Try 7 days free',
  cierreMicrocopy: '7 days free · no commitment · cancel whenever you like',
  cierreEnterpriseA: 'More than 30 locations? ',
  cierreEnterpriseCta: 'Contact sales',
  kickerPorQue: 'Why it matters',
};

const restauracionEn = {
  titulo: 'Restaurants',
  hero: {
    badge: 'For restaurant groups with more than one location',
    titulo: 'Make every one of your restaurants the ',
    acento: 'no. 1 in its area',
    sub: 'Answer reviews, post offers and watch your position on Google Maps across all your locations at once, without repeating the work at each one.',
    cta: 'Try 7 days free',
    ctaMulti: 'See how it works with several locations',
    listaTitulo: 'Your locations',
    listaEtiqueta: '4 restaurants',
    aviso: 'North site is the one that needs attention most right now',
    locales: [
      { clave: 'centro', n: 'Centre site', i: 'C', nivel: NIVELES.en.lider, p: '84', tono: 'bien' },
      { clave: 'puerto', n: 'Harbour site', i: 'H', nivel: NIVELES.en.bien, p: '71', tono: 'bien' },
      { clave: 'ensanche', n: 'Uptown site', i: 'U', nivel: NIVELES.en.podio, p: '58', tono: 'medio' },
      { clave: 'norte', n: 'North site', i: 'N', nivel: NIVELES.en.inicio, p: '34', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'With one location you notice when you lose positions on Google Maps. With ten, you have no idea which one is slipping.',
    p1: 'In hospitality the reviews never stop. Every shift, every weekend, every event adds new reviews, across all your locations at once. And each one left unanswered is not just a pending task, it is a signal Google uses to decide what position you come up in when someone searches for a restaurant like yours.',
    p2: 'With a single location it is already hard to keep up. With several, the problem multiplies: while one location holds its place, another can be losing positions without anyone noticing, because nobody is watching all ten at once. Each site competes on its own against the restaurants on its own street.',
  },
  razones: {
    kicker: 'Why restaurants need it',
    items: [
      {
        clave: 'visibilidadSeo', t: 'SEO visibility',
        d: 'Hardly anyone searches for a restaurant by name before they have been. They search “sushi near me” or “where to eat well around here”, and that is where you find out whether they can find you.',
      },
      {
        clave: 'automatizaciones', t: 'Automations',
        d: 'With the volume of reviews a restaurant gets between weekdays and weekends, answering by hand does not scale. The positive ones get answered on their own, and if a bad one comes in, you hear about it in minutes, not days.',
      },
      {
        clave: 'publicacionesIa', t: 'AI posts',
        d: 'The menu changes, there are weekend offers, live music nights. Post it in seconds and schedule it in advance across all your locations at once.',
      },
    ],
  },
  panel: {
    titulo: 'One dashboard, all your restaurants',
    cuerpo: 'You compare how each location is doing, see which one needs attention first and apply the same rules to all of them without setting them up one by one. The full detail of how multi-location management works is on the Multi-location page.',
  },
  porQue: {
    titulo: 'In hospitality, the generic search matters more than in almost any other sector',
    cuerpo: 'People rarely search for a restaurant by name before they have been, especially if they are passing through, on holiday or trying something new in a neighbourhood they do not know. That makes showing up in the local pack, the top three Google Maps results, matter even more here than in businesses where the customer already knows who they are looking for.',
    widget: 'podio',
    pie: 'Only the first three are visible without expanding',
  },
  faq: [
    {
      q: 'Does it work for a single restaurant, or do I need several locations?',
      a: 'It works just as well for a single one. Everything you see here applies the same; multi-location management is an extra if you have more than one, not a requirement.',
    },
    {
      q: 'Does it work the same for a new restaurant that does not have many reviews yet?',
      a: 'Yes. The scanner and the SEO analysis work from day one, and the more reviews you build up, the more signals the system has to help you climb.',
    },
    {
      q: 'Can I schedule weekend offer posts in advance?',
      a: 'Yes, you pick the date and time and it publishes on its own, up to 180 days ahead. If the offer comes round again, you duplicate the previous post and change the date.',
    },
    {
      q: 'I have several locations — can I compare how they are performing?',
      a: 'Yes, there is a ranking of your locations by SEO score: you see at a glance which is doing best and which needs attention, and you open the full analysis of whichever you want.',
    },
    {
      q: 'Does the reviews widget work if my website is only for bookings, with no blog or anything else?',
      a: 'Yes, it is a snippet of code you paste wherever you like on your site; it does not need any particular structure behind it.',
    },
  ],
  cierre: {
    titulo: 'Make every one of your restaurants compete to be the ',
    acento: 'no. 1 in its area',
    sub: 'Connect your Google listings, scan each area and let reviews and posts take care of themselves across all your locations.',
  },
};

const clinicasEn = {
  titulo: 'Clinics',
  hero: {
    badge: 'For clinics and health centres with several sites',
    titulo: 'Make every one of your clinics the ',
    acento: 'no. 1 in its area',
    sub: 'Answer reviews with professional care, post updates and watch your position on Google Maps across all your sites at once, without repeating the work at each one.',
    cta: 'Try 7 days free',
    ctaMulti: 'See how it works with several sites',
    listaTitulo: 'Your sites',
    listaEtiqueta: '4 clinics',
    aviso: 'North clinic is the one that needs attention most right now',
    locales: [
      { clave: 'centro', n: 'Central clinic', i: 'C', nivel: NIVELES.en.lider, p: '86', tono: 'bien' },
      { clave: 'este', n: 'East clinic', i: 'E', nivel: NIVELES.en.bien, p: '73', tono: 'bien' },
      { clave: 'ensanche', n: 'Uptown clinic', i: 'U', nivel: NIVELES.en.podio, p: '57', tono: 'medio' },
      { clave: 'norte', n: 'North clinic', i: 'N', nivel: NIVELES.en.inicio, p: '31', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'With one clinic you notice when you lose positions on Google Maps. With several, you have no idea which one is slipping.',
    p1: 'When someone searches “dentist near me” or “physio in my area”, they almost never look past the first results. And every review left unanswered is a signal Google uses to decide whether your clinic shows up there or not.',
    p2: 'With a single clinic it is already hard to keep up. With several sites the problem multiplies: while one holds its position, another can be slipping without anyone noticing. Each site competes alone in its own neighbourhood, and without an overall picture you do not know which one needs help until it has already lost ground to the clinic next door.',
  },
  razones: {
    kicker: 'Why clinics need it',
    items: [
      {
        clave: 'visibilidadSeo', t: 'SEO visibility',
        d: 'Nobody searches for “García Dental Clinic” before they know it. They search “dentist near me” or “physio in my area”, and those searches are exactly the ones the SEO map covers.',
      },
      {
        clave: 'automatizaciones', t: 'Automations',
        d: 'You set the tone once — professional and measured — and it applies to every reply. And you decide which ratings get answered automatically: the usual setup here is to automate the good ones and leave 1 to 3 stars on “alert only”, so no sensitive review gets a reply you did not write yourself.',
      },
      {
        clave: 'publicacionesIa', t: 'AI posts',
        d: 'A new specialist joining, a change of opening hours, a check-up campaign. It goes out in seconds and is scheduled in advance across all your sites at once.',
      },
    ],
  },
  panel: {
    titulo: 'One dashboard, all your clinics',
    cuerpo: 'You compare how each site is doing, see which one needs attention first and apply the same professional tone to all of them without setting it up site by site. The full detail of how multi-site management works is on the Multi-location page.',
  },
  porQue: {
    titulo: 'In healthcare, the decision is thought through more, and compared more',
    cuerpo: 'Choosing a dentist or a physio is not a quick decision. Most people compare several options before deciding, and reviews together with the position on Google Maps are among the first things they look at to narrow that list down. Showing up among the first does not just bring more visits to your listing: it decides how many options you will be compared against in the first place.',
    widget: 'comparativa',
    pie: 'The list they compare before booking',
    items: [
      { clave: 'tuya', n: 'Your clinic', destacada: true },
      { clave: 'allado', n: 'The clinic next door' },
      { clave: 'barrio', n: 'The neighbourhood centre' },
      { clave: 'otra', n: 'Another option', atenuada: true },
    ],
  },
  faq: [
    {
      q: 'Does it work for a single clinic, or do I need several sites?',
      a: 'It works just as well for a single one. Multi-site management is an extra if you have more than one, not a requirement.',
    },
    {
      q: 'How much control do I have over what an automatic reply says?',
      a: 'As much as you set up. You choose exactly which ratings get answered on their own and which stay as an email alert for you to answer yourself, and you set the brand tone the AI uses. The usual setup in healthcare is to automate only the good reviews and handle 1 to 3 stars by hand.',
    },
    {
      q: 'Can I post when a new specialist joins or when there is a campaign?',
      a: 'Yes, you schedule the post in advance, or leave it to publish right away.',
    },
    {
      q: 'I have several sites — can I compare how they are performing?',
      a: 'Yes, there is a ranking of your sites by SEO score: you see at a glance which is doing best and which needs attention, without going in site by site.',
    },
    {
      q: 'Does the reviews widget work if my website has online appointment booking?',
      a: 'Yes, it is a snippet of code you paste wherever you like on your site, next to the booking button if you want.',
    },
  ],
  cierre: {
    titulo: 'Make every one of your clinics compete to be the ',
    acento: 'no. 1 in its area',
    sub: 'Connect your Google listings, scan each area and let reviews and posts take care of themselves across all your sites.',
  },
};

const automotrizEn = {
  titulo: 'Automotive',
  hero: {
    badge: 'For garages and dealerships with several sites',
    titulo: 'Make every one of your garages the ',
    acento: 'no. 1 in its area',
    sub: 'Answer reviews, post your seasonal campaigns and watch your position on Google Maps across all your sites at once, without repeating the work at each one.',
    cta: 'Try 7 days free',
    ctaMulti: 'See how it works with several sites',
    listaTitulo: 'Your sites',
    listaEtiqueta: '4 garages',
    aviso: 'North garage is the one that needs attention most right now',
    locales: [
      { clave: 'centro', n: 'Centre garage', i: 'C', nivel: NIVELES.en.lider, p: '85', tono: 'bien' },
      { clave: 'poligono', n: 'Business park garage', i: 'P', nivel: NIVELES.en.bien, p: '72', tono: 'bien' },
      { clave: 'este', n: 'East garage', i: 'E', nivel: NIVELES.en.podio, p: '56', tono: 'medio' },
      { clave: 'norte', n: 'North garage', i: 'N', nivel: NIVELES.en.inicio, p: '33', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'With one garage you notice when you lose positions on Google Maps. With several, you have no idea which one is slipping.',
    p1: 'When the car plays up or a service is due, hardly anyone has a lifelong go-to garage. They search “garage near me” or “tyre change in my area”, and decide between the first results they get. Every review left unanswered is a signal Google uses to decide whether you show up there.',
    p2: 'With a single garage it is already hard to keep up. With several sites the problem multiplies: while one holds its position, another can be slipping without anyone noticing. Each site competes alone in its own neighbourhood, and without an overall picture you do not know which one needs help until it has already lost ground to the garage next door.',
  },
  razones: {
    kicker: 'Why the automotive trade needs it',
    items: [
      {
        clave: 'visibilidadSeo', t: 'SEO visibility',
        d: 'Nobody searches for your garage by name before they have been. They search “garage near me” or “MOT in my area”, and those searches are exactly the ones the SEO map covers.',
      },
      {
        clave: 'automatizaciones', t: 'Automations',
        d: 'Oil changes, services, MOTs: every visit generates a review, and the volume spikes in season. The positive ones get answered on their own, and if a bad one comes in, you hear about it in minutes, not days.',
      },
      {
        clave: 'publicacionesIa', t: 'AI posts',
        d: 'A seasonal tyre change, a free check before the holidays, a new make you have started to handle. Post it in seconds and schedule it in advance across all your sites at once.',
      },
    ],
  },
  panel: {
    titulo: 'One dashboard, all your garages',
    cuerpo: 'You compare how each site is doing, see which one needs attention first and apply the same rules to all of them without setting them up one by one. The full detail of how multi-site management works is on the Multi-location page.',
  },
  porQue: {
    titulo: 'In the motor trade, trust weighs more than price',
    cuerpo: 'Most customers do not know mechanics, so they cannot judge whether the diagnosis or the price is fair. All they have to decide who to trust is what others have already said: reviews and the position on Google Maps do that job for them before they walk through the door.',
    widget: 'balanza',
    pie: 'What really makes them choose',
    items: [
      { clave: 'confianza', l: 'Trust', pct: 88, destacada: true },
      { clave: 'precio', l: 'Price', pct: 34 },
    ],
  },
  faq: [
    {
      q: 'Does it work for a single garage, or do I need several sites?',
      a: 'It works just as well for a single one. Multi-site management is an extra if you have more than one, not a requirement.',
    },
    {
      q: 'Does it work the same for a new garage that does not have many reviews yet?',
      a: 'Yes. The scanner and the SEO analysis work from day one, and the more reviews you build up, the more signals the system has to help you climb.',
    },
    {
      q: 'Can I schedule seasonal campaigns in advance?',
      a: 'Yes, you pick whatever date and time you want, up to 180 days ahead, and it publishes on its own at all the sites you choose.',
    },
    {
      q: 'I have several sites — can I compare how they are performing?',
      a: 'Yes, there is a ranking of your sites by SEO score: you see at a glance which is doing best and which needs attention, without going in site by site.',
    },
    {
      q: 'Does the reviews widget work if my website has online appointment booking?',
      a: 'Yes, it is a snippet of code you paste wherever you like on your site, next to the booking button if you want.',
    },
  ],
  cierre: {
    titulo: 'Make every one of your garages compete to be the ',
    acento: 'no. 1 in its area',
    sub: 'Connect your Google listings, scan each area and let reviews and posts take care of themselves across all your sites.',
  },
};

const hotelesEn = {
  titulo: 'Hotels',
  hero: {
    badge: 'For hotels and hotel chains with several properties',
    titulo: 'Make every one of your hotels the ',
    acento: 'no. 1 in its area',
    sub: 'Answer reviews in any language, post your seasonal promotions and watch your position on Google Maps across all your properties at once, without repeating the work at each one.',
    cta: 'Try 7 days free',
    ctaMulti: 'See how it works with several properties',
    listaTitulo: 'Your properties',
    listaEtiqueta: '4 hotels',
    aviso: 'Hotel Bay is the one that needs attention most right now',
    locales: [
      { clave: 'centro', n: 'Hotel Centre', i: 'C', nivel: NIVELES.en.lider, p: '87', tono: 'bien' },
      { clave: 'puerto', n: 'Hotel Harbour', i: 'H', nivel: NIVELES.en.bien, p: '74', tono: 'bien' },
      { clave: 'sierra', n: 'Hotel Sierra', i: 'S', nivel: NIVELES.en.podio, p: '59', tono: 'medio' },
      { clave: 'bahia', n: 'Hotel Bay', i: 'B', nivel: NIVELES.en.inicio, p: '32', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'With one hotel you notice when you lose positions on Google Maps. With several, you have no idea which one is slipping.',
    p1: 'Someone looking for a hotel is hardly ever a regular. They are someone who does not know the city and decides between the first results Google shows them, with no previous loyalty to fall back on. Every unanswered review is a signal that weighs on whether you show up there or not.',
    p2: 'With a single hotel it is already hard to keep up, especially in high season when the reviews spike all at once. With several properties the problem multiplies: while one holds its position, another can be slipping without anyone noticing, because nobody is watching everyone’s positions at the same time.',
  },
  razones: {
    kicker: 'Why hotels need it',
    items: [
      {
        clave: 'visibilidadSeo', t: 'SEO visibility',
        d: 'Hardly anyone searches for your hotel by name the first time. They search “hotel in [city]” or “hotel near [area]”, with no previous loyalty, and those searches are exactly the ones the SEO map covers.',
      },
      {
        clave: 'automatizaciones', t: 'Automations',
        d: 'Reviews arrive in several languages depending on where each guest is from. In AI mode you can have each reply come out in the language the review was written in, keeping the same brand tone. And in high season, when they spike all at once, none of them gets left behind.',
      },
      {
        clave: 'publicacionesIa', t: 'AI posts',
        d: 'A last-minute offer, a weekend package, a new facility at the hotel. Post it in seconds and schedule it in advance across all your properties at once.',
      },
    ],
  },
  panel: {
    titulo: 'One dashboard, all your hotels',
    cuerpo: 'You compare how each property is doing, see which one needs attention first and apply the same rules to all of them without setting them up one by one. The full detail of how multi-site management works is on the Multi-location page.',
  },
  porQue: {
    titulo: 'With hotels, hardly anyone decides out of previous loyalty',
    cuerpo: 'Unlike a restaurant in your neighbourhood or a garage you trust, a hotel is almost always chosen among strangers, comparing several options at once, often from another city or country. That is where showing up among the first results and having recent, well-answered reviews weighs most: it is practically the only thing a traveller has to go on.',
    widget: 'idiomas',
    pie: 'Reviews from the same week, four languages',
    items: [
      { clave: 'es', k: 'ES', t: 'Todo perfecto, volveremos…' },
      { clave: 'en', k: 'EN', t: 'Lovely stay, great staff…' },
      { clave: 'de', k: 'DE', t: 'Sehr schönes Hotel…' },
      { clave: 'fr', k: 'FR', t: 'Très bon accueil…' },
    ],
  },
  faq: [
    {
      q: 'Does it work for a single hotel, or do I need several properties?',
      a: 'It works just as well for a single one. Multi-site management is an extra if you have more than one property, not a requirement.',
    },
    {
      q: 'Can it answer reviews in the language they are written in?',
      a: 'Yes. In AI mode there is a setting that makes the reply come out in the language of the review rather than yours, so a German guest gets their reply in German without you doing anything.',
    },
    {
      q: 'Does it hold up through the review spikes of high season?',
      a: 'Yes, the reply rules and the alerts work the same whether there is one review a day or fifty.',
    },
    {
      q: 'I have several properties — can I compare how they are performing?',
      a: 'Yes, there is a ranking of your properties by SEO score: you see at a glance which is doing best and which needs attention, without going in one by one.',
    },
    {
      q: 'Does the reviews widget work with my own booking engine?',
      a: 'Yes, it is a snippet of code you paste wherever you like on your site, next to the booking engine if you want.',
    },
  ],
  cierre: {
    titulo: 'Make every one of your hotels compete to be the ',
    acento: 'no. 1 in its area',
    sub: 'Connect your Google listings, scan each area and let reviews and posts take care of themselves across all your properties.',
  },
};

const comunPt = {
  verProducto: 'Ver',
  panelEnlace: 'Ver Multilocalização',
  faqTitulo: 'Perguntas frequentes',
  cierreCta: 'Testar 7 dias grátis',
  cierreMicrocopy: '7 dias grátis · sem compromisso · cancela quando quiser',
  cierreEnterpriseA: 'Mais de 30 locais? ',
  cierreEnterpriseCta: 'Fale com vendas',
  kickerPorQue: 'Porque importa',
};

const restauracionPt = {
  titulo: 'Restauração',
  hero: {
    badge: 'Para grupos de restauração com mais do que um local',
    titulo: 'Posicione cada um dos seus restaurantes como o ',
    acento: 'nº 1 da sua zona',
    sub: 'Responda a avaliações, publique promoções e vigie a sua posição no Google Maps em todos os seus locais ao mesmo tempo, sem repetir o trabalho em cada um.',
    cta: 'Testar 7 dias grátis',
    ctaMulti: 'Ver como funciona com vários locais',
    listaTitulo: 'Os seus locais',
    listaEtiqueta: '4 restaurantes',
    aviso: 'O Local Norte é o que mais precisa de atenção agora',
    locales: [
      { clave: 'centro', n: 'Local Centro', i: 'C', nivel: NIVELES.pt.lider, p: '84', tono: 'bien' },
      { clave: 'puerto', n: 'Local Porto', i: 'P', nivel: NIVELES.pt.bien, p: '71', tono: 'bien' },
      { clave: 'ensanche', n: 'Local Avenida', i: 'A', nivel: NIVELES.pt.podio, p: '58', tono: 'medio' },
      { clave: 'norte', n: 'Local Norte', i: 'N', nivel: NIVELES.pt.inicio, p: '34', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Com um local, nota-se se perde posições no Google Maps. Com dez, não sabe qual está a cair.',
    p1: 'Na restauração as avaliações não param de chegar. Cada turno, cada fim de semana, cada evento soma avaliações novas, em todos os seus locais ao mesmo tempo. E cada uma que fica sem resposta não é só uma tarefa pendente, é um sinal que o Google usa para decidir em que posição aparece quando alguém procura um restaurante como o seu.',
    p2: 'Com um só local já custa manter isso em dia. Com vários, o problema multiplica-se: enquanto um local mantém o seu lugar, outro pode estar a cair posições sem que ninguém dê por isso, porque ninguém está a olhar para os dez ao mesmo tempo. Cada sede compete por sua conta contra os restaurantes da sua própria rua.',
  },
  razones: {
    kicker: 'Porque a restauração precisa disto',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilidade SEO',
        d: 'Quase ninguém procura um restaurante pelo nome antes de lá ter ido. Procura «sushi perto de mim» ou «onde comer bem por aqui», e é aí que se joga se o encontram ou não.',
      },
      {
        clave: 'automatizaciones', t: 'Automatizações',
        d: 'Com o volume de avaliações que um restaurante gera entre semana e fim de semana, responder à mão não escala. As positivas respondem-se sozinhas, e se chegar uma má, fica a saber em minutos, não em dias.',
      },
      {
        clave: 'publicacionesIa', t: 'Publicações com IA',
        d: 'O menu muda, há promoções de fim de semana, eventos com música ao vivo. Publique em segundos e agende com antecedência em todos os seus locais ao mesmo tempo.',
      },
    ],
  },
  panel: {
    titulo: 'Um painel, todos os seus restaurantes',
    cuerpo: 'Compara o desempenho de cada local, vê qual precisa de atenção primeiro e aplica as mesmas regras a todos sem as configurar um a um. O detalhe completo de como funciona a gestão multilocal está em Multilocalização.',
  },
  porQue: {
    titulo: 'Na restauração, a pesquisa genérica pesa mais do que em quase qualquer outro setor',
    cuerpo: 'Raramente alguém procura um restaurante pelo nome antes de lá ter ido, sobretudo se está de passagem, em turismo ou a experimentar algo novo num bairro que não conhece. Isso faz com que aparecer no pacote local, os três primeiros resultados do Google Maps, importe ainda mais aqui do que em negócios onde o cliente já sabe quem procura.',
    widget: 'podio',
    pie: 'Só os três primeiros se veem sem abrir mais',
  },
  faq: [
    {
      q: 'Serve para um só restaurante ou é preciso ter vários locais?',
      a: 'Funciona igualmente bem para um só. Tudo o que vê aqui aplica-se da mesma forma; a gestão multilocal é um extra se tiver mais do que um local, não um requisito.',
    },
    {
      q: 'Funciona igual para um restaurante novo, ainda com poucas avaliações?',
      a: 'Sim. O scanner e a análise de SEO funcionam desde o primeiro dia, e quantas mais avaliações for somando, mais sinais o sistema tem para o ajudar a subir.',
    },
    {
      q: 'Posso agendar publicações de promoções de fim de semana com antecedência?',
      a: 'Sim, escolhe a data e a hora e publica-se sozinha, até com 180 dias de antecedência. Se a promoção se repetir, duplica-se a publicação anterior e muda-se a data.',
    },
    {
      q: 'Tenho vários locais, posso comparar o desempenho entre eles?',
      a: 'Sim, há um ranking entre os seus locais por pontuação SEO: vê num relance qual vai melhor e qual precisa de atenção, e entra na análise completa do que quiser.',
    },
    {
      q: 'O widget de avaliações serve se o meu site for só de reservas, sem blogue nem mais nada?',
      a: 'Sim, é um pedaço de código que cola onde quiser no seu site; não precisa de nenhuma estrutura especial por trás.',
    },
  ],
  cierre: {
    titulo: 'Que cada um dos seus restaurantes compita por ser o ',
    acento: 'nº 1 da sua zona',
    sub: 'Ligue as suas fichas do Google, analise cada zona e deixe que as avaliações e as publicações se giram sozinhas em todos os seus locais.',
  },
};

const clinicasPt = {
  titulo: 'Clínicas',
  hero: {
    badge: 'Para clínicas e centros de saúde com várias sedes',
    titulo: 'Posicione cada uma das suas clínicas como o ',
    acento: 'nº 1 da sua zona',
    sub: 'Responda a avaliações com prudência profissional, publique novidades e vigie a sua posição no Google Maps em todas as suas sedes ao mesmo tempo, sem repetir o trabalho em cada uma.',
    cta: 'Testar 7 dias grátis',
    ctaMulti: 'Ver como funciona com várias sedes',
    listaTitulo: 'As suas sedes',
    listaEtiqueta: '4 clínicas',
    aviso: 'A Sede Norte é a que mais precisa de atenção agora',
    locales: [
      { clave: 'centro', n: 'Sede Centro', i: 'C', nivel: NIVELES.pt.lider, p: '86', tono: 'bien' },
      { clave: 'este', n: 'Sede Este', i: 'E', nivel: NIVELES.pt.bien, p: '73', tono: 'bien' },
      { clave: 'ensanche', n: 'Sede Avenida', i: 'A', nivel: NIVELES.pt.podio, p: '57', tono: 'medio' },
      { clave: 'norte', n: 'Sede Norte', i: 'N', nivel: NIVELES.pt.inicio, p: '31', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Com uma clínica, nota-se se perde posições no Google Maps. Com várias, não sabe qual está a cair.',
    p1: 'Quando alguém procura «dentista perto de mim» ou «fisioterapeuta na minha zona», quase nunca olha para além dos primeiros resultados. E cada avaliação que fica sem resposta é um sinal que o Google usa para decidir se a sua clínica aparece ali ou não.',
    p2: 'Com uma só clínica já custa mantê-lo em dia. Com várias sedes, o problema multiplica-se: enquanto uma mantém a sua posição, outra pode estar a cair sem que ninguém dê por isso. Cada sede compete sozinha no seu próprio bairro, e sem visão de conjunto não sabe qual precisa de ajuda até já ter perdido terreno para a clínica ao lado.',
  },
  razones: {
    kicker: 'Porque as clínicas precisam disto',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilidade SEO',
        d: 'Ninguém procura «Clínica Dentária García» antes de a conhecer. Procura «dentista perto de mim» ou «fisioterapeuta na minha zona», e essas pesquisas são precisamente as que o Mapa de SEO cobre.',
      },
      {
        clave: 'automatizaciones', t: 'Automatizações',
        d: 'Define o tom uma vez — profissional e prudente — e aplica-se a todas as respostas. E decide que classificações se respondem sozinhas: o habitual aqui é automatizar as boas e deixar as de 1 a 3 estrelas em «só aviso», para que nenhuma avaliação delicada receba uma resposta que não tenha escrito.',
      },
      {
        clave: 'publicacionesIa', t: 'Publicações com IA',
        d: 'Um novo especialista que chega, uma mudança de horário, uma campanha de rastreios. Publica-se em segundos e agenda-se com antecedência em todas as suas sedes ao mesmo tempo.',
      },
    ],
  },
  panel: {
    titulo: 'Um painel, todas as suas clínicas',
    cuerpo: 'Compara o desempenho de cada sede, vê qual precisa de atenção primeiro e aplica o mesmo tom profissional a todas sem o configurar sede a sede. O detalhe completo de como funciona a gestão multi-sede está em Multilocalização.',
  },
  porQue: {
    titulo: 'Na saúde, a decisão pensa-se mais, e compara-se mais',
    cuerpo: 'Escolher dentista ou fisioterapeuta não é uma decisão rápida. A maioria compara várias opções antes de decidir, e as avaliações, a par da posição no Google Maps, são das primeiras coisas que olha para reduzir essa lista. Aparecer entre os primeiros não traz só mais visitas à sua ficha: decide desde logo quantas opções o vão comparar.',
    widget: 'comparativa',
    pie: 'A lista que se compara antes de marcar',
    items: [
      { clave: 'tuya', n: 'A sua clínica', destacada: true },
      { clave: 'allado', n: 'A clínica ao lado' },
      { clave: 'barrio', n: 'O centro do bairro' },
      { clave: 'otra', n: 'Outra opção', atenuada: true },
    ],
  },
  faq: [
    {
      q: 'Serve para uma só clínica ou é preciso ter várias sedes?',
      a: 'Funciona igualmente bem para uma só. A gestão multi-sede é um extra se tiver mais do que uma, não um requisito.',
    },
    {
      q: 'Que controlo tenho sobre o que publica uma resposta automática?',
      a: 'O que configurar. Escolhe exatamente que classificações se respondem sozinhas e quais ficam em aviso por email para que as responda, e define o tom de marca que a IA usa. O habitual na saúde é automatizar apenas as avaliações boas e tratar à mão as de 1 a 3 estrelas.',
    },
    {
      q: 'Posso publicar quando chega um novo especialista ou há uma campanha?',
      a: 'Sim, agenda a publicação com antecedência, ou deixa-a para publicar de imediato.',
    },
    {
      q: 'Tenho várias sedes, posso comparar o desempenho entre elas?',
      a: 'Sim, há um ranking entre as suas sedes por pontuação SEO: vê num relance qual vai melhor e qual precisa de atenção, sem entrar sede a sede.',
    },
    {
      q: 'O widget de avaliações funciona se o meu site tiver marcação online?',
      a: 'Sim, é um pedaço de código que cola onde quiser no seu site, ao lado do botão de marcação se quiser.',
    },
  ],
  cierre: {
    titulo: 'Que cada uma das suas clínicas compita por ser o ',
    acento: 'nº 1 da sua zona',
    sub: 'Ligue as suas fichas do Google, analise cada zona e deixe que as avaliações e as publicações se giram sozinhas em todas as suas sedes.',
  },
};

const automotrizPt = {
  titulo: 'Automóvel',
  hero: {
    badge: 'Para oficinas e concessionários com várias sedes',
    titulo: 'Posicione cada uma das suas oficinas como o ',
    acento: 'nº 1 da sua zona',
    sub: 'Responda a avaliações, publique as suas campanhas de época e vigie a sua posição no Google Maps em todas as suas sedes ao mesmo tempo, sem repetir o trabalho em cada uma.',
    cta: 'Testar 7 dias grátis',
    ctaMulti: 'Ver como funciona com várias sedes',
    listaTitulo: 'As suas sedes',
    listaEtiqueta: '4 oficinas',
    aviso: 'A Oficina Norte é a que mais precisa de atenção agora',
    locales: [
      { clave: 'centro', n: 'Oficina Centro', i: 'C', nivel: NIVELES.pt.lider, p: '85', tono: 'bien' },
      { clave: 'poligono', n: 'Oficina Zona Industrial', i: 'Z', nivel: NIVELES.pt.bien, p: '72', tono: 'bien' },
      { clave: 'este', n: 'Oficina Este', i: 'E', nivel: NIVELES.pt.podio, p: '56', tono: 'medio' },
      { clave: 'norte', n: 'Oficina Norte', i: 'N', nivel: NIVELES.pt.inicio, p: '33', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Com uma oficina, nota-se se perde posições no Google Maps. Com várias, não sabe qual está a cair.',
    p1: 'Quando o carro avaria ou está na altura da revisão, quase ninguém tem uma oficina de sempre. Procura «oficina perto de mim» ou «mudança de pneus na minha zona», e decide entre os primeiros resultados que lhe aparecem. Cada avaliação que fica sem resposta é um sinal que o Google usa para decidir se aparece ali.',
    p2: 'Com uma só oficina já custa mantê-lo em dia. Com várias sedes, o problema multiplica-se: enquanto uma mantém a sua posição, outra pode estar a cair sem que ninguém dê por isso. Cada sede compete sozinha no seu próprio bairro, e sem visão de conjunto não sabe qual precisa de ajuda até já ter perdido terreno para a oficina ao lado.',
  },
  razones: {
    kicker: 'Porque o setor automóvel precisa disto',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilidade SEO',
        d: 'Ninguém procura a sua oficina pelo nome antes de lá ter ido. Procura «oficina perto de mim» ou «inspeção na minha zona», e essas pesquisas são precisamente as que o Mapa de SEO cobre.',
      },
      {
        clave: 'automatizaciones', t: 'Automatizações',
        d: 'Mudanças de óleo, revisões, inspeções: cada visita gera uma avaliação, e o volume dispara em época alta. As positivas respondem-se sozinhas, e se chegar uma má, fica a saber em minutos, não em dias.',
      },
      {
        clave: 'publicacionesIa', t: 'Publicações com IA',
        d: 'Mudança de pneus de época, revisão gratuita antes das férias, uma nova marca que passa a trabalhar. Publique em segundos e agende com antecedência em todas as suas sedes ao mesmo tempo.',
      },
    ],
  },
  panel: {
    titulo: 'Um painel, todas as suas oficinas',
    cuerpo: 'Compara o desempenho de cada sede, vê qual precisa de atenção primeiro e aplica as mesmas regras a todas sem as configurar uma a uma. O detalhe completo de como funciona a gestão multi-sede está em Multilocalização.',
  },
  porQue: {
    titulo: 'No setor automóvel, a confiança pesa mais do que o preço',
    cuerpo: 'A maioria dos clientes não percebe de mecânica, por isso não consegue julgar se o diagnóstico ou o preço são justos. O único que tem para decidir em quem confiar é o que outros já contaram: as avaliações e a posição no Google Maps fazem esse trabalho por ele antes de entrar pela porta.',
    widget: 'balanza',
    pie: 'O que de facto o faz escolher',
    items: [
      { clave: 'confianza', l: 'Confiança', pct: 88, destacada: true },
      { clave: 'precio', l: 'Preço', pct: 34 },
    ],
  },
  faq: [
    {
      q: 'Serve para uma só oficina ou é preciso ter várias sedes?',
      a: 'Funciona igualmente bem para uma só. A gestão multi-sede é um extra se tiver mais do que uma, não um requisito.',
    },
    {
      q: 'Funciona igual para uma oficina nova, ainda com poucas avaliações?',
      a: 'Sim. O scanner e a análise de SEO funcionam desde o primeiro dia, e quantas mais avaliações for somando, mais sinais o sistema tem para o ajudar a subir.',
    },
    {
      q: 'Posso agendar as campanhas de época com antecedência?',
      a: 'Sim, escolhe a data e a hora que quiser, até com 180 dias de antecedência, e publica-se sozinha em todas as sedes que escolher.',
    },
    {
      q: 'Tenho várias sedes, posso comparar o desempenho entre elas?',
      a: 'Sim, há um ranking entre as suas sedes por pontuação SEO: vê num relance qual vai melhor e qual precisa de atenção, sem entrar sede a sede.',
    },
    {
      q: 'O widget de avaliações funciona se o meu site tiver marcação online?',
      a: 'Sim, é um pedaço de código que cola onde quiser no seu site, ao lado do botão de marcação se quiser.',
    },
  ],
  cierre: {
    titulo: 'Que cada uma das suas oficinas compita por ser o ',
    acento: 'nº 1 da sua zona',
    sub: 'Ligue as suas fichas do Google, analise cada zona e deixe que as avaliações e as publicações se giram sozinhas em todas as suas sedes.',
  },
};

const hotelesPt = {
  titulo: 'Hotéis',
  hero: {
    badge: 'Para hotéis e cadeias hoteleiras com vários estabelecimentos',
    titulo: 'Posicione cada um dos seus hotéis como o ',
    acento: 'nº 1 da sua zona',
    sub: 'Responda a avaliações em qualquer idioma, publique as suas promoções de época e vigie a sua posição no Google Maps em todos os seus estabelecimentos ao mesmo tempo, sem repetir o trabalho em cada um.',
    cta: 'Testar 7 dias grátis',
    ctaMulti: 'Ver como funciona com vários estabelecimentos',
    listaTitulo: 'Os seus estabelecimentos',
    listaEtiqueta: '4 hotéis',
    aviso: 'O Hotel Baía é o que mais precisa de atenção agora',
    locales: [
      { clave: 'centro', n: 'Hotel Centro', i: 'C', nivel: NIVELES.pt.lider, p: '87', tono: 'bien' },
      { clave: 'puerto', n: 'Hotel Porto', i: 'P', nivel: NIVELES.pt.bien, p: '74', tono: 'bien' },
      { clave: 'sierra', n: 'Hotel Serra', i: 'S', nivel: NIVELES.pt.podio, p: '59', tono: 'medio' },
      { clave: 'bahia', n: 'Hotel Baía', i: 'B', nivel: NIVELES.pt.inicio, p: '32', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Com um hotel, nota-se se perde posições no Google Maps. Com vários, não sabe qual está a cair.',
    p1: 'Quem procura hotel quase nunca é um cliente habitual. É alguém que não conhece a cidade e decide entre os primeiros resultados que o Google lhe mostra, sem nenhuma fidelidade prévia a que se agarrar. Cada avaliação sem resposta é um sinal que pesa em se aparece ali ou não.',
    p2: 'Com um só hotel já custa mantê-lo em dia, sobretudo em época alta, quando as avaliações disparam de uma vez. Com vários estabelecimentos o problema multiplica-se: enquanto um mantém a sua posição, outro pode estar a cair sem que ninguém dê por isso, porque ninguém está a olhar para as posições de todos ao mesmo tempo.',
  },
  razones: {
    kicker: 'Porque os hotéis precisam disto',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilidade SEO',
        d: 'Quase ninguém procura o seu hotel pelo nome à primeira. Procura «hotel em [cidade]» ou «hotel perto de [zona]», sem nenhuma fidelidade prévia, e essas pesquisas são precisamente as que o Mapa de SEO cobre.',
      },
      {
        clave: 'automatizaciones', t: 'Automatizações',
        d: 'As avaliações chegam em vários idiomas consoante a origem de cada hóspede. Com o modo IA pode fazer com que cada resposta saia no idioma em que a avaliação foi escrita, mantendo o mesmo tom de marca. E em época alta, quando disparam de uma vez, nenhuma fica para trás.',
      },
      {
        clave: 'publicacionesIa', t: 'Publicações com IA',
        d: 'Uma promoção de última hora, um pacote de fim de semana, uma nova instalação do hotel. Publique em segundos e agende com antecedência em todos os seus estabelecimentos ao mesmo tempo.',
      },
    ],
  },
  panel: {
    titulo: 'Um painel, todos os seus hotéis',
    cuerpo: 'Compara o desempenho de cada estabelecimento, vê qual precisa de atenção primeiro e aplica as mesmas regras a todos sem as configurar um a um. O detalhe completo de como funciona a gestão multi-sede está em Multilocalização.',
  },
  porQue: {
    titulo: 'Nos hotéis, quase ninguém decide com fidelidade prévia',
    cuerpo: 'Ao contrário de um restaurante do seu bairro ou de uma oficina de confiança, um hotel escolhe-se quase sempre entre desconhecidos, comparando várias opções ao mesmo tempo, muitas vezes a partir de outra cidade ou país. É aí que mais pesa aparecer entre os primeiros resultados e ter avaliações recentes e bem respondidas: é praticamente a única coisa em que o viajante se pode apoiar para decidir.',
    widget: 'idiomas',
    pie: 'Avaliações da mesma semana, quatro idiomas',
    items: [
      { clave: 'es', k: 'ES', t: 'Todo perfecto, volveremos…' },
      { clave: 'en', k: 'EN', t: 'Lovely stay, great staff…' },
      { clave: 'de', k: 'DE', t: 'Sehr schönes Hotel…' },
      { clave: 'fr', k: 'FR', t: 'Très bon accueil…' },
    ],
  },
  faq: [
    {
      q: 'Serve para um só hotel ou é preciso ter vários estabelecimentos?',
      a: 'Funciona igualmente bem para um só. A gestão multi-sede é um extra se tiver mais do que um estabelecimento, não um requisito.',
    },
    {
      q: 'Consegue responder a avaliações no idioma em que são escritas?',
      a: 'Sim. No modo IA há um ajuste que faz com que a resposta saia no idioma da avaliação em vez do seu, por isso um hóspede alemão recebe a resposta em alemão sem que tenha de fazer nada.',
    },
    {
      q: 'Aguenta bem os picos de avaliações da época alta?',
      a: 'Sim, as regras de resposta e os alertas funcionam igual haja uma avaliação por dia ou cinquenta.',
    },
    {
      q: 'Tenho vários estabelecimentos, posso comparar o desempenho entre eles?',
      a: 'Sim, há um ranking entre os seus estabelecimentos por pontuação SEO: vê num relance qual vai melhor e qual precisa de atenção, sem entrar um a um.',
    },
    {
      q: 'O widget de avaliações funciona com o meu motor de reservas próprio?',
      a: 'Sim, é um pedaço de código que cola onde quiser no seu site, ao lado do motor de reservas se quiser.',
    },
  ],
  cierre: {
    titulo: 'Que cada um dos seus hotéis compita por ser o ',
    acento: 'nº 1 da sua zona',
    sub: 'Ligue as suas fichas do Google, analise cada zona e deixe que as avaliações e as publicações se giram sozinhas em todos os seus estabelecimentos.',
  },
};

const comunDe = {
  verProducto: 'Ansehen',
  panelEnlace: 'Mehrere Standorte ansehen',
  faqTitulo: 'Häufige Fragen',
  cierreCta: '7 Tage kostenlos testen',
  cierreMicrocopy: '7 Tage kostenlos · unverbindlich · jederzeit kündbar',
  cierreEnterpriseA: 'Mehr als 30 Standorte? ',
  cierreEnterpriseCta: 'Vertrieb kontaktieren',
  kickerPorQue: 'Warum das zählt',
};

const restauracionDe = {
  titulo: 'Gastronomie',
  hero: {
    badge: 'Für Gastronomiegruppen mit mehr als einem Standort',
    titulo: 'Mach jedes deiner Restaurants zur ',
    acento: 'Nr. 1 seiner Gegend',
    sub: 'Beantworte Bewertungen, veröffentliche Angebote und behalte deine Position bei Google Maps an allen Standorten gleichzeitig im Blick, ohne die Arbeit an jedem einzelnen zu wiederholen.',
    cta: '7 Tage kostenlos testen',
    ctaMulti: 'Ansehen, wie es mit mehreren Standorten funktioniert',
    listaTitulo: 'Deine Standorte',
    listaEtiqueta: '4 Restaurants',
    aviso: 'Standort Nord braucht gerade am dringendsten Aufmerksamkeit',
    locales: [
      { clave: 'centro', n: 'Standort Mitte', i: 'M', nivel: NIVELES.de.lider, p: '84', tono: 'bien' },
      { clave: 'puerto', n: 'Standort Hafen', i: 'H', nivel: NIVELES.de.bien, p: '71', tono: 'bien' },
      { clave: 'ensanche', n: 'Standort Altstadt', i: 'A', nivel: NIVELES.de.podio, p: '58', tono: 'medio' },
      { clave: 'norte', n: 'Standort Nord', i: 'N', nivel: NIVELES.de.inicio, p: '34', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Mit einem Standort merkst du, wenn du bei Google Maps Positionen verlierst. Mit zehn weißt du nicht, welcher abrutscht.',
    p1: 'In der Gastronomie hören die Bewertungen nie auf. Jede Schicht, jedes Wochenende, jede Veranstaltung bringt neue Bewertungen, an allen deinen Standorten gleichzeitig. Und jede unbeantwortete ist nicht nur eine offene Aufgabe, sondern ein Signal, mit dem Google entscheidet, auf welcher Position du erscheinst, wenn jemand ein Restaurant wie deins sucht.',
    p2: 'Schon bei einem einzigen Standort ist das kaum aktuell zu halten. Bei mehreren vervielfacht sich das Problem: Während ein Standort seinen Platz hält, kann ein anderer Positionen verlieren, ohne dass es jemand merkt — weil niemand alle zehn gleichzeitig im Blick hat. Jeder Standort kämpft für sich gegen die Restaurants in seiner eigenen Straße.',
  },
  razones: {
    kicker: 'Warum die Gastronomie das braucht',
    items: [
      {
        clave: 'visibilidadSeo', t: 'SEO-Sichtbarkeit',
        d: 'Kaum jemand sucht ein Restaurant beim Namen, bevor er dort war. Gesucht wird „Sushi in meiner Nähe“ oder „wo kann man hier gut essen“ — und genau da entscheidet sich, ob man dich findet.',
      },
      {
        clave: 'automatizaciones', t: 'Automatisierungen',
        d: 'Bei der Menge an Bewertungen, die ein Restaurant unter der Woche und am Wochenende bekommt, skaliert Handarbeit nicht. Die positiven werden von allein beantwortet, und kommt eine schlechte, erfährst du es in Minuten, nicht in Tagen.',
      },
      {
        clave: 'publicacionesIa', t: 'KI-Beiträge',
        d: 'Die Karte ändert sich, es gibt Wochenendangebote, Abende mit Livemusik. Veröffentliche es in Sekunden und plane es im Voraus für alle deine Standorte gleichzeitig.',
      },
    ],
  },
  panel: {
    titulo: 'Ein Dashboard, alle deine Restaurants',
    cuerpo: 'Du vergleichst, wie jeder Standort läuft, siehst, welcher zuerst Aufmerksamkeit braucht, und wendest dieselben Regeln auf alle an, ohne sie einzeln einzurichten. Wie die Verwaltung mehrerer Standorte genau funktioniert, steht auf der Seite „Mehrere Standorte“.',
  },
  porQue: {
    titulo: 'In der Gastronomie wiegt die allgemeine Suche schwerer als in fast jeder anderen Branche',
    cuerpo: 'Selten sucht jemand ein Restaurant beim Namen, bevor er dort war — erst recht nicht, wenn er auf der Durchreise ist, Urlaub macht oder in einem unbekannten Viertel etwas Neues probiert. Deshalb zählt es hier noch mehr als in Branchen, in denen der Kunde schon weiß, wen er sucht, im Local Pack zu erscheinen, den ersten drei Google-Maps-Ergebnissen.',
    widget: 'podio',
    pie: 'Nur die ersten drei sind ohne Aufklappen zu sehen',
  },
  faq: [
    {
      q: 'Taugt das für ein einzelnes Restaurant, oder braucht man mehrere Standorte?',
      a: 'Es funktioniert für ein einzelnes genauso gut. Alles, was du hier siehst, gilt gleichermaßen; die Verwaltung mehrerer Standorte ist ein Extra, wenn du mehr als einen hast, keine Voraussetzung.',
    },
    {
      q: 'Funktioniert es genauso für ein neues Restaurant, das noch wenige Bewertungen hat?',
      a: 'Ja. Der Scanner und die SEO-Analyse funktionieren ab dem ersten Tag, und je mehr Bewertungen dazukommen, desto mehr Signale hat das System, um dir beim Aufstieg zu helfen.',
    },
    {
      q: 'Kann ich Beiträge zu Wochenendangeboten im Voraus planen?',
      a: 'Ja, du wählst Datum und Uhrzeit und es wird von allein veröffentlicht, bis zu 180 Tage im Voraus. Wenn das Angebot wiederkommt, dupliziert man den vorigen Beitrag und ändert das Datum.',
    },
    {
      q: 'Ich habe mehrere Standorte — kann ich vergleichen, wie sie laufen?',
      a: 'Ja, es gibt ein Ranking deiner Standorte nach SEO-Punktzahl: Du siehst auf einen Blick, welcher am besten läuft und welcher Aufmerksamkeit braucht, und öffnest die vollständige Analyse von dem, den du willst.',
    },
    {
      q: 'Taugt das Bewertungs-Widget, wenn meine Website nur zum Reservieren da ist, ohne Blog oder sonst etwas?',
      a: 'Ja, es ist ein Stück Code, das du einfügst, wo du willst; es braucht keine besondere Struktur dahinter.',
    },
  ],
  cierre: {
    titulo: 'Dass jedes deiner Restaurants darum kämpft, die ',
    acento: 'Nr. 1 seiner Gegend',
    sub: 'Verbinde deine Google-Profile, scanne jede Gegend und lass Bewertungen und Beiträge sich an allen deinen Standorten von allein erledigen.',
  },
};

const clinicasDe = {
  titulo: 'Praxen und Kliniken',
  hero: {
    badge: 'Für Praxen und Gesundheitszentren mit mehreren Standorten',
    titulo: 'Mach jede deiner Praxen zur ',
    acento: 'Nr. 1 ihrer Gegend',
    sub: 'Beantworte Bewertungen mit fachlicher Umsicht, veröffentliche Neuigkeiten und behalte deine Position bei Google Maps an allen Standorten gleichzeitig im Blick, ohne die Arbeit an jedem einzelnen zu wiederholen.',
    cta: '7 Tage kostenlos testen',
    ctaMulti: 'Ansehen, wie es mit mehreren Standorten funktioniert',
    listaTitulo: 'Deine Standorte',
    listaEtiqueta: '4 Praxen',
    aviso: 'Standort Nord braucht gerade am dringendsten Aufmerksamkeit',
    locales: [
      { clave: 'centro', n: 'Standort Mitte', i: 'M', nivel: NIVELES.de.lider, p: '86', tono: 'bien' },
      { clave: 'este', n: 'Standort Ost', i: 'O', nivel: NIVELES.de.bien, p: '73', tono: 'bien' },
      { clave: 'ensanche', n: 'Standort Altstadt', i: 'A', nivel: NIVELES.de.podio, p: '57', tono: 'medio' },
      { clave: 'norte', n: 'Standort Nord', i: 'N', nivel: NIVELES.de.inicio, p: '31', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Mit einer Praxis merkst du, wenn du bei Google Maps Positionen verlierst. Mit mehreren weißt du nicht, welche abrutscht.',
    p1: 'Wenn jemand „Zahnarzt in meiner Nähe“ oder „Physiotherapie in meiner Gegend“ sucht, schaut er fast nie über die ersten Ergebnisse hinaus. Und jede unbeantwortete Bewertung ist ein Signal, mit dem Google entscheidet, ob deine Praxis dort auftaucht oder nicht.',
    p2: 'Schon bei einer einzigen Praxis ist das kaum aktuell zu halten. Bei mehreren Standorten vervielfacht sich das Problem: Während einer seine Position hält, kann ein anderer abrutschen, ohne dass es jemand merkt. Jeder Standort kämpft allein in seinem eigenen Viertel, und ohne Gesamtbild weißt du erst, welcher Hilfe braucht, wenn er gegenüber der Praxis nebenan schon Boden verloren hat.',
  },
  razones: {
    kicker: 'Warum Praxen das brauchen',
    items: [
      {
        clave: 'visibilidadSeo', t: 'SEO-Sichtbarkeit',
        d: 'Niemand sucht „Zahnarztpraxis García“, bevor er sie kennt. Gesucht wird „Zahnarzt in meiner Nähe“ oder „Physiotherapie in meiner Gegend“ — und genau diese Suchen deckt die SEO-Karte ab.',
      },
      {
        clave: 'automatizaciones', t: 'Automatisierungen',
        d: 'Du legst den Ton einmal fest — fachlich und zurückhaltend — und er gilt für alle Antworten. Und du entscheidest, welche Bewertungen von allein beantwortet werden: üblich ist hier, die guten zu automatisieren und 1 bis 3 Sterne auf „nur Hinweis“ zu lassen, damit keine heikle Bewertung eine Antwort bekommt, die nicht von dir stammt.',
      },
      {
        clave: 'publicacionesIa', t: 'KI-Beiträge',
        d: 'Eine neue Fachkraft, geänderte Sprechzeiten, eine Vorsorgeaktion. Das geht in Sekunden raus und wird im Voraus für alle deine Standorte gleichzeitig geplant.',
      },
    ],
  },
  panel: {
    titulo: 'Ein Dashboard, alle deine Praxen',
    cuerpo: 'Du vergleichst, wie jeder Standort läuft, siehst, welcher zuerst Aufmerksamkeit braucht, und wendest denselben fachlichen Ton auf alle an, ohne ihn Standort für Standort einzurichten. Wie die Verwaltung mehrerer Standorte genau funktioniert, steht auf der Seite „Mehrere Standorte“.',
  },
  porQue: {
    titulo: 'Im Gesundheitsbereich wird länger überlegt — und mehr verglichen',
    cuerpo: 'Zahnarzt oder Physiotherapie auszusuchen ist keine schnelle Entscheidung. Die meisten vergleichen mehrere Optionen, bevor sie sich entscheiden, und Bewertungen zusammen mit der Position bei Google Maps gehören zum Ersten, worauf sie schauen, um diese Liste einzugrenzen. Unter den Ersten zu erscheinen bringt nicht nur mehr Aufrufe deines Profils: Es entscheidet gleich mit, gegen wie viele Optionen du überhaupt verglichen wirst.',
    widget: 'comparativa',
    pie: 'Die Liste, die man vor der Terminbuchung vergleicht',
    items: [
      { clave: 'tuya', n: 'Deine Praxis', destacada: true },
      { clave: 'allado', n: 'Die Praxis nebenan' },
      { clave: 'barrio', n: 'Das Zentrum im Viertel' },
      { clave: 'otra', n: 'Eine weitere Option', atenuada: true },
    ],
  },
  faq: [
    {
      q: 'Taugt das für eine einzelne Praxis, oder braucht man mehrere Standorte?',
      a: 'Es funktioniert für eine einzelne genauso gut. Die Verwaltung mehrerer Standorte ist ein Extra, wenn du mehr als eine hast, keine Voraussetzung.',
    },
    {
      q: 'Wie viel Kontrolle habe ich darüber, was eine automatische Antwort veröffentlicht?',
      a: 'So viel, wie du einrichtest. Du wählst genau, welche Bewertungen von allein beantwortet werden und welche als E-Mail-Hinweis liegen bleiben, damit du sie selbst beantwortest — und du legst den Markenton fest, den die KI verwendet. Im Gesundheitsbereich ist üblich, nur die guten Bewertungen zu automatisieren und 1 bis 3 Sterne von Hand zu behandeln.',
    },
    {
      q: 'Kann ich etwas veröffentlichen, wenn eine neue Fachkraft anfängt oder eine Aktion läuft?',
      a: 'Ja, du planst den Beitrag im Voraus oder lässt ihn sofort veröffentlichen.',
    },
    {
      q: 'Ich habe mehrere Standorte — kann ich vergleichen, wie sie laufen?',
      a: 'Ja, es gibt ein Ranking deiner Standorte nach SEO-Punktzahl: Du siehst auf einen Blick, welcher am besten läuft und welcher Aufmerksamkeit braucht, ohne jeden einzeln aufzurufen.',
    },
    {
      q: 'Funktioniert das Bewertungs-Widget, wenn meine Website eine Online-Terminbuchung hat?',
      a: 'Ja, es ist ein Stück Code, das du einfügst, wo du willst — auf Wunsch direkt neben dem Buchungsbutton.',
    },
  ],
  cierre: {
    titulo: 'Dass jede deiner Praxen darum kämpft, die ',
    acento: 'Nr. 1 ihrer Gegend',
    sub: 'Verbinde deine Google-Profile, scanne jede Gegend und lass Bewertungen und Beiträge sich an allen deinen Standorten von allein erledigen.',
  },
};

const automotrizDe = {
  titulo: 'Kfz-Branche',
  hero: {
    badge: 'Für Werkstätten und Autohäuser mit mehreren Standorten',
    titulo: 'Mach jede deiner Werkstätten zur ',
    acento: 'Nr. 1 ihrer Gegend',
    sub: 'Beantworte Bewertungen, veröffentliche deine Saisonaktionen und behalte deine Position bei Google Maps an allen Standorten gleichzeitig im Blick, ohne die Arbeit an jedem einzelnen zu wiederholen.',
    cta: '7 Tage kostenlos testen',
    ctaMulti: 'Ansehen, wie es mit mehreren Standorten funktioniert',
    listaTitulo: 'Deine Standorte',
    listaEtiqueta: '4 Werkstätten',
    aviso: 'Werkstatt Nord braucht gerade am dringendsten Aufmerksamkeit',
    locales: [
      { clave: 'centro', n: 'Werkstatt Mitte', i: 'M', nivel: NIVELES.de.lider, p: '85', tono: 'bien' },
      { clave: 'poligono', n: 'Werkstatt Gewerbegebiet', i: 'G', nivel: NIVELES.de.bien, p: '72', tono: 'bien' },
      { clave: 'este', n: 'Werkstatt Ost', i: 'O', nivel: NIVELES.de.podio, p: '56', tono: 'medio' },
      { clave: 'norte', n: 'Werkstatt Nord', i: 'N', nivel: NIVELES.de.inicio, p: '33', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Mit einer Werkstatt merkst du, wenn du bei Google Maps Positionen verlierst. Mit mehreren weißt du nicht, welche abrutscht.',
    p1: 'Wenn das Auto streikt oder die Inspektion ansteht, hat kaum jemand seine Stammwerkstatt fürs Leben. Gesucht wird „Werkstatt in meiner Nähe“ oder „Reifenwechsel in meiner Gegend“, und entschieden wird zwischen den ersten Treffern. Jede unbeantwortete Bewertung ist ein Signal, mit dem Google entscheidet, ob du dort auftauchst.',
    p2: 'Schon bei einer einzigen Werkstatt ist das kaum aktuell zu halten. Bei mehreren Standorten vervielfacht sich das Problem: Während einer seine Position hält, kann ein anderer abrutschen, ohne dass es jemand merkt. Jeder Standort kämpft allein in seinem eigenen Viertel, und ohne Gesamtbild weißt du erst, welcher Hilfe braucht, wenn er gegenüber der Werkstatt nebenan schon Boden verloren hat.',
  },
  razones: {
    kicker: 'Warum die Kfz-Branche das braucht',
    items: [
      {
        clave: 'visibilidadSeo', t: 'SEO-Sichtbarkeit',
        d: 'Niemand sucht deine Werkstatt beim Namen, bevor er dort war. Gesucht wird „Werkstatt in meiner Nähe“ oder „HU in meiner Gegend“ — und genau diese Suchen deckt die SEO-Karte ab.',
      },
      {
        clave: 'automatizaciones', t: 'Automatisierungen',
        d: 'Ölwechsel, Inspektionen, HU: Jeder Besuch erzeugt eine Bewertung, und in der Saison schnellt die Menge hoch. Die positiven werden von allein beantwortet, und kommt eine schlechte, erfährst du es in Minuten, nicht in Tagen.',
      },
      {
        clave: 'publicacionesIa', t: 'KI-Beiträge',
        d: 'Saisonaler Reifenwechsel, kostenloser Check vor dem Urlaub, eine neue Marke, die du jetzt betreust. Veröffentliche es in Sekunden und plane es im Voraus für alle deine Standorte gleichzeitig.',
      },
    ],
  },
  panel: {
    titulo: 'Ein Dashboard, alle deine Werkstätten',
    cuerpo: 'Du vergleichst, wie jeder Standort läuft, siehst, welcher zuerst Aufmerksamkeit braucht, und wendest dieselben Regeln auf alle an, ohne sie einzeln einzurichten. Wie die Verwaltung mehrerer Standorte genau funktioniert, steht auf der Seite „Mehrere Standorte“.',
  },
  porQue: {
    titulo: 'In der Kfz-Branche wiegt Vertrauen schwerer als der Preis',
    cuerpo: 'Die meisten Kunden verstehen nichts von Technik und können deshalb nicht beurteilen, ob Diagnose oder Preis fair sind. Das Einzige, woran sie sich halten können, um zu entscheiden, wem sie vertrauen, ist das, was andere schon erzählt haben: Bewertungen und die Position bei Google Maps übernehmen diese Arbeit, bevor jemand zur Tür hereinkommt.',
    widget: 'balanza',
    pie: 'Was die Entscheidung wirklich bestimmt',
    items: [
      { clave: 'confianza', l: 'Vertrauen', pct: 88, destacada: true },
      { clave: 'precio', l: 'Preis', pct: 34 },
    ],
  },
  faq: [
    {
      q: 'Taugt das für eine einzelne Werkstatt, oder braucht man mehrere Standorte?',
      a: 'Es funktioniert für eine einzelne genauso gut. Die Verwaltung mehrerer Standorte ist ein Extra, wenn du mehr als eine hast, keine Voraussetzung.',
    },
    {
      q: 'Funktioniert es genauso für eine neue Werkstatt, die noch wenige Bewertungen hat?',
      a: 'Ja. Der Scanner und die SEO-Analyse funktionieren ab dem ersten Tag, und je mehr Bewertungen dazukommen, desto mehr Signale hat das System, um dir beim Aufstieg zu helfen.',
    },
    {
      q: 'Kann ich die Saisonaktionen im Voraus planen?',
      a: 'Ja, du wählst Datum und Uhrzeit frei, bis zu 180 Tage im Voraus, und es wird an allen Standorten, die du auswählst, von allein veröffentlicht.',
    },
    {
      q: 'Ich habe mehrere Standorte — kann ich vergleichen, wie sie laufen?',
      a: 'Ja, es gibt ein Ranking deiner Standorte nach SEO-Punktzahl: Du siehst auf einen Blick, welcher am besten läuft und welcher Aufmerksamkeit braucht, ohne jeden einzeln aufzurufen.',
    },
    {
      q: 'Funktioniert das Bewertungs-Widget, wenn meine Website eine Online-Terminbuchung hat?',
      a: 'Ja, es ist ein Stück Code, das du einfügst, wo du willst — auf Wunsch direkt neben dem Terminbutton.',
    },
  ],
  cierre: {
    titulo: 'Dass jede deiner Werkstätten darum kämpft, die ',
    acento: 'Nr. 1 ihrer Gegend',
    sub: 'Verbinde deine Google-Profile, scanne jede Gegend und lass Bewertungen und Beiträge sich an allen deinen Standorten von allein erledigen.',
  },
};

const hotelesDe = {
  titulo: 'Hotels',
  hero: {
    badge: 'Für Hotels und Hotelketten mit mehreren Häusern',
    titulo: 'Mach jedes deiner Hotels zur ',
    acento: 'Nr. 1 seiner Gegend',
    sub: 'Beantworte Bewertungen in jeder Sprache, veröffentliche deine Saisonangebote und behalte deine Position bei Google Maps in allen Häusern gleichzeitig im Blick, ohne die Arbeit in jedem einzelnen zu wiederholen.',
    cta: '7 Tage kostenlos testen',
    ctaMulti: 'Ansehen, wie es mit mehreren Häusern funktioniert',
    listaTitulo: 'Deine Häuser',
    listaEtiqueta: '4 Hotels',
    aviso: 'Hotel Bucht braucht gerade am dringendsten Aufmerksamkeit',
    locales: [
      { clave: 'centro', n: 'Hotel Mitte', i: 'M', nivel: NIVELES.de.lider, p: '87', tono: 'bien' },
      { clave: 'puerto', n: 'Hotel Hafen', i: 'H', nivel: NIVELES.de.bien, p: '74', tono: 'bien' },
      { clave: 'sierra', n: 'Hotel Sierra', i: 'S', nivel: NIVELES.de.podio, p: '59', tono: 'medio' },
      { clave: 'bahia', n: 'Hotel Bucht', i: 'B', nivel: NIVELES.de.inicio, p: '32', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Mit einem Hotel merkst du, wenn du bei Google Maps Positionen verlierst. Mit mehreren weißt du nicht, welches abrutscht.',
    p1: 'Wer ein Hotel sucht, ist fast nie Stammgast. Es ist jemand, der die Stadt nicht kennt und zwischen den ersten Treffern entscheidet, die Google ihm zeigt — ohne jede frühere Bindung, an der er sich festhalten könnte. Jede unbeantwortete Bewertung ist ein Signal, das mitentscheidet, ob du dort erscheinst.',
    p2: 'Schon bei einem einzigen Hotel ist das kaum aktuell zu halten, besonders in der Hochsaison, wenn die Bewertungen auf einen Schlag hochgehen. Bei mehreren Häusern vervielfacht sich das Problem: Während eines seine Position hält, kann ein anderes abrutschen, ohne dass es jemand merkt — weil niemand die Positionen aller gleichzeitig im Blick hat.',
  },
  razones: {
    kicker: 'Warum Hotels das brauchen',
    items: [
      {
        clave: 'visibilidadSeo', t: 'SEO-Sichtbarkeit',
        d: 'Kaum jemand sucht dein Hotel beim ersten Mal namentlich. Gesucht wird „Hotel in [Stadt]“ oder „Hotel nahe [Gegend]“, ganz ohne frühere Bindung — und genau diese Suchen deckt die SEO-Karte ab.',
      },
      {
        clave: 'automatizaciones', t: 'Automatisierungen',
        d: 'Die Bewertungen kommen in mehreren Sprachen, je nachdem, woher der Gast stammt. Im KI-Modus kannst du jede Antwort in der Sprache herauskommen lassen, in der die Bewertung geschrieben wurde — im selben Markenton. Und in der Hochsaison, wenn sie auf einen Schlag hochgehen, bleibt keine liegen.',
      },
      {
        clave: 'publicacionesIa', t: 'KI-Beiträge',
        d: 'Ein Last-Minute-Angebot, ein Wochenendpaket, eine neue Einrichtung im Haus. Veröffentliche es in Sekunden und plane es im Voraus für alle deine Häuser gleichzeitig.',
      },
    ],
  },
  panel: {
    titulo: 'Ein Dashboard, alle deine Hotels',
    cuerpo: 'Du vergleichst, wie jedes Haus läuft, siehst, welches zuerst Aufmerksamkeit braucht, und wendest dieselben Regeln auf alle an, ohne sie einzeln einzurichten. Wie die Verwaltung mehrerer Standorte genau funktioniert, steht auf der Seite „Mehrere Standorte“.',
  },
  porQue: {
    titulo: 'Bei Hotels entscheidet kaum jemand aus früherer Bindung',
    cuerpo: 'Anders als ein Restaurant im eigenen Viertel oder eine Werkstatt des Vertrauens wird ein Hotel fast immer unter Unbekannten ausgesucht — mehrere Optionen auf einmal verglichen, oft aus einer anderen Stadt oder einem anderen Land. Genau da wiegt es am schwersten, unter den ersten Ergebnissen zu erscheinen und aktuelle, gut beantwortete Bewertungen zu haben: Es ist praktisch das Einzige, worauf sich Reisende stützen können.',
    widget: 'idiomas',
    pie: 'Bewertungen aus derselben Woche, vier Sprachen',
    items: [
      { clave: 'es', k: 'ES', t: 'Todo perfecto, volveremos…' },
      { clave: 'en', k: 'EN', t: 'Lovely stay, great staff…' },
      { clave: 'de', k: 'DE', t: 'Sehr schönes Hotel…' },
      { clave: 'fr', k: 'FR', t: 'Très bon accueil…' },
    ],
  },
  faq: [
    {
      q: 'Taugt das für ein einzelnes Hotel, oder braucht man mehrere Häuser?',
      a: 'Es funktioniert für ein einzelnes genauso gut. Die Verwaltung mehrerer Standorte ist ein Extra, wenn du mehr als ein Haus hast, keine Voraussetzung.',
    },
    {
      q: 'Kann es Bewertungen in der Sprache beantworten, in der sie geschrieben sind?',
      a: 'Ja. Im KI-Modus gibt es eine Einstellung, die die Antwort in der Sprache der Bewertung statt in deiner herauskommen lässt — ein deutscher Gast bekommt seine Antwort also auf Deutsch, ohne dass du etwas tun musst.',
    },
    {
      q: 'Hält es die Bewertungsspitzen der Hochsaison aus?',
      a: 'Ja, die Antwortregeln und die Hinweise funktionieren gleich, ob eine Bewertung am Tag kommt oder fünfzig.',
    },
    {
      q: 'Ich habe mehrere Häuser — kann ich vergleichen, wie sie laufen?',
      a: 'Ja, es gibt ein Ranking deiner Häuser nach SEO-Punktzahl: Du siehst auf einen Blick, welches am besten läuft und welches Aufmerksamkeit braucht, ohne jedes einzeln aufzurufen.',
    },
    {
      q: 'Funktioniert das Bewertungs-Widget mit meiner eigenen Buchungsmaschine?',
      a: 'Ja, es ist ein Stück Code, das du einfügst, wo du willst — auf Wunsch direkt neben der Buchungsmaschine.',
    },
  ],
  cierre: {
    titulo: 'Dass jedes deiner Hotels darum kämpft, die ',
    acento: 'Nr. 1 seiner Gegend',
    sub: 'Verbinde deine Google-Profile, scanne jede Gegend und lass Bewertungen und Beiträge sich in allen deinen Häusern von allein erledigen.',
  },
};

const comunFr = {
  verProducto: 'Voir',
  panelEnlace: 'Voir Multi-établissements',
  faqTitulo: 'Questions fréquentes',
  cierreCta: 'Essayer 7 jours gratuits',
  cierreMicrocopy: '7 jours gratuits · sans engagement · annulez quand vous voulez',
  cierreEnterpriseA: 'Plus de 30 établissements ? ',
  cierreEnterpriseCta: 'Contacter les ventes',
  kickerPorQue: 'Pourquoi c’est important',
};

const restauracionFr = {
  titulo: 'Restauration',
  hero: {
    badge: 'Pour les groupes de restauration à plusieurs établissements',
    titulo: 'Positionnez chacun de vos restaurants comme le ',
    acento: 'nº 1 de sa zone',
    sub: 'Répondez aux avis, publiez vos offres et surveillez votre position sur Google Maps dans tous vos établissements à la fois, sans répéter le travail dans chacun.',
    cta: 'Essayer 7 jours gratuits',
    ctaMulti: 'Voir comment ça marche avec plusieurs établissements',
    listaTitulo: 'Vos établissements',
    listaEtiqueta: '4 restaurants',
    aviso: 'C’est l’établissement Nord qui a le plus besoin d’attention en ce moment',
    locales: [
      { clave: 'centro', n: 'Établissement Centre', i: 'C', nivel: NIVELES.fr.lider, p: '84', tono: 'bien' },
      { clave: 'puerto', n: 'Établissement Port', i: 'P', nivel: NIVELES.fr.bien, p: '71', tono: 'bien' },
      { clave: 'ensanche', n: 'Établissement Sud', i: 'S', nivel: NIVELES.fr.podio, p: '58', tono: 'medio' },
      { clave: 'norte', n: 'Établissement Nord', i: 'N', nivel: NIVELES.fr.inicio, p: '34', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Avec un établissement, vous voyez si vous perdez des positions sur Google Maps. Avec dix, vous ne savez pas lequel décroche.',
    p1: 'En restauration, les avis n’arrêtent jamais. Chaque service, chaque week-end, chaque événement ajoute de nouveaux avis, dans tous vos établissements à la fois. Et chacun laissé sans réponse n’est pas qu’une tâche en attente : c’est un signal que Google utilise pour décider à quelle position vous apparaissez quand quelqu’un cherche un restaurant comme le vôtre.',
    p2: 'Avec un seul établissement, c’est déjà difficile à tenir à jour. Avec plusieurs, le problème se multiplie : pendant qu’un établissement garde sa place, un autre peut perdre des positions sans que personne ne le remarque, parce que personne ne regarde les dix à la fois. Chaque site se bat seul contre les restaurants de sa propre rue.',
  },
  razones: {
    kicker: 'Pourquoi la restauration en a besoin',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilité SEO',
        d: 'Presque personne ne cherche un restaurant par son nom avant d’y être allé. On cherche « sushi près de moi » ou « où bien manger par ici », et c’est là que tout se joue.',
      },
      {
        clave: 'automatizaciones', t: 'Automatisations',
        d: 'Avec le volume d’avis que génère un restaurant en semaine et le week-end, répondre à la main ne tient pas. Les avis positifs reçoivent une réponse tout seuls, et si un mauvais arrive, vous l’apprenez en quelques minutes, pas en quelques jours.',
      },
      {
        clave: 'publicacionesIa', t: 'Publications IA',
        d: 'La carte change, il y a des offres du week-end, des soirées musique live. Publiez-le en quelques secondes et programmez-le à l’avance dans tous vos établissements à la fois.',
      },
    ],
  },
  panel: {
    titulo: 'Un tableau de bord, tous vos restaurants',
    cuerpo: 'Vous comparez les performances de chaque établissement, voyez lequel a besoin d’attention en premier et appliquez les mêmes règles à tous sans les configurer un par un. Le détail complet de la gestion multi-établissements est sur la page Multi-établissements.',
  },
  porQue: {
    titulo: 'En restauration, la recherche générique pèse plus que dans presque tout autre secteur',
    cuerpo: 'Il est rare que quelqu’un cherche un restaurant par son nom avant d’y être allé, surtout s’il est de passage, en vacances ou en train d’essayer quelque chose de nouveau dans un quartier qu’il ne connaît pas. Apparaître dans le pack local, les trois premiers résultats de Google Maps, compte donc encore plus ici que dans les activités où le client sait déjà qui il cherche.',
    widget: 'podio',
    pie: 'Seuls les trois premiers sont visibles sans dérouler',
  },
  faq: [
    {
      q: 'Est-ce utile pour un seul restaurant, ou faut-il plusieurs établissements ?',
      a: 'Cela fonctionne aussi bien pour un seul. Tout ce que vous voyez ici s’applique de la même façon ; la gestion multi-établissements est un plus si vous en avez plusieurs, pas une condition.',
    },
    {
      q: 'Est-ce que cela fonctionne pareil pour un restaurant récent, avec encore peu d’avis ?',
      a: 'Oui. Le scanner et l’analyse SEO fonctionnent dès le premier jour, et plus vous accumulez d’avis, plus le système a de signaux pour vous aider à monter.',
    },
    {
      q: 'Puis-je programmer à l’avance les publications d’offres du week-end ?',
      a: 'Oui, vous choisissez la date et l’heure et cela se publie tout seul, jusqu’à 180 jours à l’avance. Si l’offre revient, on duplique la publication précédente et on change la date.',
    },
    {
      q: 'J’ai plusieurs établissements, puis-je comparer leurs performances ?',
      a: 'Oui, il y a un classement de vos établissements par note SEO : vous voyez d’un coup d’œil lequel va le mieux et lequel a besoin d’attention, et vous ouvrez l’analyse complète de celui que vous voulez.',
    },
    {
      q: 'Le widget d’avis sert-il si mon site ne fait que de la réservation, sans blog ni rien d’autre ?',
      a: 'Oui, c’est un bout de code que vous collez où vous voulez sur votre site ; il n’a besoin d’aucune structure particulière derrière.',
    },
  ],
  cierre: {
    titulo: 'Que chacun de vos restaurants se batte pour être le ',
    acento: 'nº 1 de sa zone',
    sub: 'Connectez vos fiches Google, analysez chaque zone et laissez les avis et les publications se gérer tout seuls dans tous vos établissements.',
  },
};

const clinicasFr = {
  titulo: 'Cliniques',
  hero: {
    badge: 'Pour les cliniques et centres de santé à plusieurs sites',
    titulo: 'Positionnez chacune de vos cliniques comme le ',
    acento: 'nº 1 de sa zone',
    sub: 'Répondez aux avis avec la prudence d’un professionnel, publiez vos actualités et surveillez votre position sur Google Maps sur tous vos sites à la fois, sans répéter le travail dans chacun.',
    cta: 'Essayer 7 jours gratuits',
    ctaMulti: 'Voir comment ça marche avec plusieurs sites',
    listaTitulo: 'Vos sites',
    listaEtiqueta: '4 cliniques',
    aviso: 'C’est le site Nord qui a le plus besoin d’attention en ce moment',
    locales: [
      { clave: 'centro', n: 'Site Centre', i: 'C', nivel: NIVELES.fr.lider, p: '86', tono: 'bien' },
      { clave: 'este', n: 'Site Est', i: 'E', nivel: NIVELES.fr.bien, p: '73', tono: 'bien' },
      { clave: 'ensanche', n: 'Site Sud', i: 'S', nivel: NIVELES.fr.podio, p: '57', tono: 'medio' },
      { clave: 'norte', n: 'Site Nord', i: 'N', nivel: NIVELES.fr.inicio, p: '31', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Avec une clinique, vous voyez si vous perdez des positions sur Google Maps. Avec plusieurs, vous ne savez pas laquelle décroche.',
    p1: 'Quand quelqu’un cherche « dentiste près de moi » ou « kiné dans ma zone », il regarde rarement au-delà des premiers résultats. Et chaque avis laissé sans réponse est un signal que Google utilise pour décider si votre clinique apparaît là ou non.',
    p2: 'Avec une seule clinique, c’est déjà difficile à tenir à jour. Avec plusieurs sites, le problème se multiplie : pendant que l’un garde sa position, un autre peut décrocher sans que personne ne le remarque. Chaque site se bat seul dans son propre quartier, et sans vision d’ensemble vous ne savez pas lequel a besoin d’aide avant qu’il n’ait déjà perdu du terrain face à la clinique d’à côté.',
  },
  razones: {
    kicker: 'Pourquoi les cliniques en ont besoin',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilité SEO',
        d: 'Personne ne cherche « Cabinet dentaire García » avant de le connaître. On cherche « dentiste près de moi » ou « kiné dans ma zone », et ce sont précisément ces recherches que couvre la carte SEO.',
      },
      {
        clave: 'automatizaciones', t: 'Automatisations',
        d: 'Vous fixez le ton une fois — professionnel et mesuré — et il s’applique à toutes les réponses. Et vous décidez quelles notes reçoivent une réponse automatique : l’usage ici est d’automatiser les bonnes et de laisser les 1 à 3 étoiles en « alerte seulement », pour qu’aucun avis délicat ne reçoive une réponse que vous n’avez pas écrite.',
      },
      {
        clave: 'publicacionesIa', t: 'Publications IA',
        d: 'Un nouveau spécialiste qui arrive, un changement d’horaires, une campagne de bilans. Cela se publie en quelques secondes et se programme à l’avance sur tous vos sites à la fois.',
      },
    ],
  },
  panel: {
    titulo: 'Un tableau de bord, toutes vos cliniques',
    cuerpo: 'Vous comparez les performances de chaque site, voyez lequel a besoin d’attention en premier et appliquez le même ton professionnel à tous sans le configurer site par site. Le détail complet de la gestion multi-sites est sur la page Multi-établissements.',
  },
  porQue: {
    titulo: 'En santé, la décision se réfléchit davantage, et se compare davantage',
    cuerpo: 'Choisir un dentiste ou un kiné n’est pas une décision rapide. La plupart des gens comparent plusieurs options avant de choisir, et les avis, avec la position sur Google Maps, font partie des premières choses qu’ils regardent pour réduire cette liste. Apparaître parmi les premiers n’apporte pas seulement plus de visites sur votre fiche : cela décide d’emblée combien d’options vous seront comparées.',
    widget: 'comparativa',
    pie: 'La liste que l’on compare avant de prendre rendez-vous',
    items: [
      { clave: 'tuya', n: 'Votre clinique', destacada: true },
      { clave: 'allado', n: 'La clinique d’à côté' },
      { clave: 'barrio', n: 'Le centre du quartier' },
      { clave: 'otra', n: 'Une autre option', atenuada: true },
    ],
  },
  faq: [
    {
      q: 'Est-ce utile pour une seule clinique, ou faut-il plusieurs sites ?',
      a: 'Cela fonctionne aussi bien pour une seule. La gestion multi-sites est un plus si vous en avez plusieurs, pas une condition.',
    },
    {
      q: 'Quel contrôle ai-je sur ce que publie une réponse automatique ?',
      a: 'Celui que vous configurez. Vous choisissez exactement quelles notes reçoivent une réponse automatique et lesquelles restent en alerte par e-mail pour que vous y répondiez vous-même, et vous fixez le ton de marque utilisé par l’IA. L’usage en santé est de n’automatiser que les bons avis et de traiter les 1 à 3 étoiles à la main.',
    },
    {
      q: 'Puis-je publier quand un nouveau spécialiste arrive ou qu’une campagne démarre ?',
      a: 'Oui, vous programmez la publication à l’avance, ou vous la laissez partir tout de suite.',
    },
    {
      q: 'J’ai plusieurs sites, puis-je comparer leurs performances ?',
      a: 'Oui, il y a un classement de vos sites par note SEO : vous voyez d’un coup d’œil lequel va le mieux et lequel a besoin d’attention, sans entrer site par site.',
    },
    {
      q: 'Le widget d’avis fonctionne-t-il si mon site a la prise de rendez-vous en ligne ?',
      a: 'Oui, c’est un bout de code que vous collez où vous voulez sur votre site, à côté du bouton de rendez-vous si vous le souhaitez.',
    },
  ],
  cierre: {
    titulo: 'Que chacune de vos cliniques se batte pour être le ',
    acento: 'nº 1 de sa zone',
    sub: 'Connectez vos fiches Google, analysez chaque zone et laissez les avis et les publications se gérer tout seuls sur tous vos sites.',
  },
};

const automotrizFr = {
  titulo: 'Automobile',
  hero: {
    badge: 'Pour les garages et concessions à plusieurs sites',
    titulo: 'Positionnez chacun de vos garages comme le ',
    acento: 'nº 1 de sa zone',
    sub: 'Répondez aux avis, publiez vos campagnes de saison et surveillez votre position sur Google Maps sur tous vos sites à la fois, sans répéter le travail dans chacun.',
    cta: 'Essayer 7 jours gratuits',
    ctaMulti: 'Voir comment ça marche avec plusieurs sites',
    listaTitulo: 'Vos sites',
    listaEtiqueta: '4 garages',
    aviso: 'C’est le garage Nord qui a le plus besoin d’attention en ce moment',
    locales: [
      { clave: 'centro', n: 'Garage Centre', i: 'C', nivel: NIVELES.fr.lider, p: '85', tono: 'bien' },
      { clave: 'poligono', n: 'Garage Zone Industrielle', i: 'Z', nivel: NIVELES.fr.bien, p: '72', tono: 'bien' },
      { clave: 'este', n: 'Garage Est', i: 'E', nivel: NIVELES.fr.podio, p: '56', tono: 'medio' },
      { clave: 'norte', n: 'Garage Nord', i: 'N', nivel: NIVELES.fr.inicio, p: '33', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Avec un garage, vous voyez si vous perdez des positions sur Google Maps. Avec plusieurs, vous ne savez pas lequel décroche.',
    p1: 'Quand la voiture lâche ou que la révision arrive, presque personne n’a un garage attitré de toujours. On cherche « garage près de moi » ou « changement de pneus dans ma zone », et on choisit parmi les premiers résultats. Chaque avis laissé sans réponse est un signal que Google utilise pour décider si vous apparaissez là.',
    p2: 'Avec un seul garage, c’est déjà difficile à tenir à jour. Avec plusieurs sites, le problème se multiplie : pendant que l’un garde sa position, un autre peut décrocher sans que personne ne le remarque. Chaque site se bat seul dans son propre quartier, et sans vision d’ensemble vous ne savez pas lequel a besoin d’aide avant qu’il n’ait déjà perdu du terrain face au garage d’à côté.',
  },
  razones: {
    kicker: 'Pourquoi l’automobile en a besoin',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilité SEO',
        d: 'Personne ne cherche votre garage par son nom avant d’y être allé. On cherche « garage près de moi » ou « contrôle technique dans ma zone », et ce sont précisément ces recherches que couvre la carte SEO.',
      },
      {
        clave: 'automatizaciones', t: 'Automatisations',
        d: 'Vidanges, révisions, contrôles techniques : chaque passage génère un avis, et le volume s’envole en saison. Les avis positifs reçoivent une réponse tout seuls, et si un mauvais arrive, vous l’apprenez en quelques minutes, pas en quelques jours.',
      },
      {
        clave: 'publicacionesIa', t: 'Publications IA',
        d: 'Changement de pneus de saison, révision gratuite avant les vacances, une nouvelle marque que vous commencez à travailler. Publiez-le en quelques secondes et programmez-le à l’avance sur tous vos sites à la fois.',
      },
    ],
  },
  panel: {
    titulo: 'Un tableau de bord, tous vos garages',
    cuerpo: 'Vous comparez les performances de chaque site, voyez lequel a besoin d’attention en premier et appliquez les mêmes règles à tous sans les configurer un par un. Le détail complet de la gestion multi-sites est sur la page Multi-établissements.',
  },
  porQue: {
    titulo: 'Dans l’automobile, la confiance pèse plus que le prix',
    cuerpo: 'La plupart des clients ne connaissent pas la mécanique, ils ne peuvent donc pas juger si le diagnostic ou le prix sont justes. La seule chose dont ils disposent pour décider à qui faire confiance, c’est ce que d’autres ont déjà raconté : les avis et la position sur Google Maps font ce travail pour eux avant qu’ils ne passent la porte.',
    widget: 'balanza',
    pie: 'Ce qui les fait vraiment choisir',
    items: [
      { clave: 'confianza', l: 'Confiance', pct: 88, destacada: true },
      { clave: 'precio', l: 'Prix', pct: 34 },
    ],
  },
  faq: [
    {
      q: 'Est-ce utile pour un seul garage, ou faut-il plusieurs sites ?',
      a: 'Cela fonctionne aussi bien pour un seul. La gestion multi-sites est un plus si vous en avez plusieurs, pas une condition.',
    },
    {
      q: 'Est-ce que cela fonctionne pareil pour un garage récent, avec encore peu d’avis ?',
      a: 'Oui. Le scanner et l’analyse SEO fonctionnent dès le premier jour, et plus vous accumulez d’avis, plus le système a de signaux pour vous aider à monter.',
    },
    {
      q: 'Puis-je programmer les campagnes de saison à l’avance ?',
      a: 'Oui, vous choisissez la date et l’heure que vous voulez, jusqu’à 180 jours à l’avance, et cela se publie tout seul sur tous les sites que vous choisissez.',
    },
    {
      q: 'J’ai plusieurs sites, puis-je comparer leurs performances ?',
      a: 'Oui, il y a un classement de vos sites par note SEO : vous voyez d’un coup d’œil lequel va le mieux et lequel a besoin d’attention, sans entrer site par site.',
    },
    {
      q: 'Le widget d’avis fonctionne-t-il si mon site a la prise de rendez-vous en ligne ?',
      a: 'Oui, c’est un bout de code que vous collez où vous voulez sur votre site, à côté du bouton de rendez-vous si vous le souhaitez.',
    },
  ],
  cierre: {
    titulo: 'Que chacun de vos garages se batte pour être le ',
    acento: 'nº 1 de sa zone',
    sub: 'Connectez vos fiches Google, analysez chaque zone et laissez les avis et les publications se gérer tout seuls sur tous vos sites.',
  },
};

const hotelesFr = {
  titulo: 'Hôtels',
  hero: {
    badge: 'Pour les hôtels et les chaînes hôtelières à plusieurs établissements',
    titulo: 'Positionnez chacun de vos hôtels comme le ',
    acento: 'nº 1 de sa zone',
    sub: 'Répondez aux avis dans n’importe quelle langue, publiez vos promotions de saison et surveillez votre position sur Google Maps dans tous vos établissements à la fois, sans répéter le travail dans chacun.',
    cta: 'Essayer 7 jours gratuits',
    ctaMulti: 'Voir comment ça marche avec plusieurs établissements',
    listaTitulo: 'Vos établissements',
    listaEtiqueta: '4 hôtels',
    aviso: 'C’est l’Hôtel Baie qui a le plus besoin d’attention en ce moment',
    locales: [
      { clave: 'centro', n: 'Hôtel Centre', i: 'C', nivel: NIVELES.fr.lider, p: '87', tono: 'bien' },
      { clave: 'puerto', n: 'Hôtel Port', i: 'P', nivel: NIVELES.fr.bien, p: '74', tono: 'bien' },
      { clave: 'sierra', n: 'Hôtel Sierra', i: 'S', nivel: NIVELES.fr.podio, p: '59', tono: 'medio' },
      { clave: 'bahia', n: 'Hôtel Baie', i: 'B', nivel: NIVELES.fr.inicio, p: '32', tono: 'mal' },
    ],
  },
  problema: {
    titulo: 'Avec un hôtel, vous voyez si vous perdez des positions sur Google Maps. Avec plusieurs, vous ne savez pas lequel décroche.',
    p1: 'Celui qui cherche un hôtel n’est presque jamais un client habituel. C’est quelqu’un qui ne connaît pas la ville et qui choisit parmi les premiers résultats que Google lui montre, sans aucune fidélité préalable à laquelle se raccrocher. Chaque avis sans réponse est un signal qui pèse sur votre présence à cet endroit.',
    p2: 'Avec un seul hôtel, c’est déjà difficile à tenir à jour, surtout en haute saison, quand les avis s’envolent d’un coup. Avec plusieurs établissements, le problème se multiplie : pendant que l’un garde sa position, un autre peut décrocher sans que personne ne le remarque, parce que personne ne regarde les positions de tous en même temps.',
  },
  razones: {
    kicker: 'Pourquoi les hôtels en ont besoin',
    items: [
      {
        clave: 'visibilidadSeo', t: 'Visibilité SEO',
        d: 'Presque personne ne cherche votre hôtel par son nom la première fois. On cherche « hôtel à [ville] » ou « hôtel près de [zone] », sans aucune fidélité préalable, et ce sont précisément ces recherches que couvre la carte SEO.',
      },
      {
        clave: 'automatizaciones', t: 'Automatisations',
        d: 'Les avis arrivent dans plusieurs langues selon la provenance de chaque client. En mode IA, vous pouvez faire sortir chaque réponse dans la langue où l’avis a été écrit, en gardant le même ton de marque. Et en haute saison, quand ils s’envolent d’un coup, aucun ne reste en arrière.',
      },
      {
        clave: 'publicacionesIa', t: 'Publications IA',
        d: 'Une offre de dernière minute, un forfait week-end, un nouvel équipement de l’hôtel. Publiez-le en quelques secondes et programmez-le à l’avance dans tous vos établissements à la fois.',
      },
    ],
  },
  panel: {
    titulo: 'Un tableau de bord, tous vos hôtels',
    cuerpo: 'Vous comparez les performances de chaque établissement, voyez lequel a besoin d’attention en premier et appliquez les mêmes règles à tous sans les configurer un par un. Le détail complet de la gestion multi-sites est sur la page Multi-établissements.',
  },
  porQue: {
    titulo: 'À l’hôtel, presque personne ne décide par fidélité préalable',
    cuerpo: 'À la différence d’un restaurant de quartier ou d’un garage de confiance, un hôtel se choisit presque toujours parmi des inconnus, en comparant plusieurs options à la fois, souvent depuis une autre ville ou un autre pays. C’est là que pèsent le plus le fait d’apparaître parmi les premiers résultats et d’avoir des avis récents et bien traités : c’est pratiquement la seule chose sur laquelle le voyageur peut s’appuyer.',
    widget: 'idiomas',
    pie: 'Des avis de la même semaine, quatre langues',
    items: [
      { clave: 'es', k: 'ES', t: 'Todo perfecto, volveremos…' },
      { clave: 'en', k: 'EN', t: 'Lovely stay, great staff…' },
      { clave: 'de', k: 'DE', t: 'Sehr schönes Hotel…' },
      { clave: 'fr', k: 'FR', t: 'Très bon accueil…' },
    ],
  },
  faq: [
    {
      q: 'Est-ce utile pour un seul hôtel, ou faut-il plusieurs établissements ?',
      a: 'Cela fonctionne aussi bien pour un seul. La gestion multi-sites est un plus si vous avez plusieurs établissements, pas une condition.',
    },
    {
      q: 'Peut-il répondre aux avis dans la langue où ils sont écrits ?',
      a: 'Oui. En mode IA, un réglage fait sortir la réponse dans la langue de l’avis plutôt que dans la vôtre : un client allemand reçoit donc sa réponse en allemand sans que vous ayez rien à faire.',
    },
    {
      q: 'Tient-il le coup face aux pics d’avis de la haute saison ?',
      a: 'Oui, les règles de réponse et les alertes fonctionnent pareil qu’il y ait un avis par jour ou cinquante.',
    },
    {
      q: 'J’ai plusieurs établissements, puis-je comparer leurs performances ?',
      a: 'Oui, il y a un classement de vos établissements par note SEO : vous voyez d’un coup d’œil lequel va le mieux et lequel a besoin d’attention, sans entrer un par un.',
    },
    {
      q: 'Le widget d’avis fonctionne-t-il avec mon propre moteur de réservation ?',
      a: 'Oui, c’est un bout de code que vous collez où vous voulez sur votre site, à côté du moteur de réservation si vous le souhaitez.',
    },
  ],
  cierre: {
    titulo: 'Que chacun de vos hôtels se batte pour être le ',
    acento: 'nº 1 de sa zone',
    sub: 'Connectez vos fiches Google, analysez chaque zone et laissez les avis et les publications se gérer tout seuls dans tous vos établissements.',
  },
};

export const sectoresV3 = {
  es: { comun, restauracion, clinicas, automotriz, hoteles },
  fr: {
    comun: comunFr,
    restauracion: restauracionFr,
    clinicas: clinicasFr,
    automotriz: automotrizFr,
    hoteles: hotelesFr,
  },
  de: {
    comun: comunDe,
    restauracion: restauracionDe,
    clinicas: clinicasDe,
    automotriz: automotrizDe,
    hoteles: hotelesDe,
  },
  pt: {
    comun: comunPt,
    restauracion: restauracionPt,
    clinicas: clinicasPt,
    automotriz: automotrizPt,
    hoteles: hotelesPt,
  },
  en: {
    comun: comunEn,
    restauracion: restauracionEn,
    clinicas: clinicasEn,
    automotriz: automotrizEn,
    hoteles: hotelesEn,
  },
};
